import {
  JourneyStage,
  JourneyMarket,
  JourneyPersona,
  JourneyEvaluation,
  PersonaDetectionContext,
  JourneyStep
} from '../types/journey';
import { getActionTranslation } from './journeyActionTranslations';

// Type for steps before they're saved to database (without DB fields)
type GeneratedStep = Omit<JourneyStep, 'id' | 'journeyId' | 'createdAt' | 'updatedAt'>;

/**
 * Helper function to add text_zh to actions that have translations
 */
function addTranslationsToActions(actions: any[]): any[] {
  return actions.map(action => {
    const translation = getActionTranslation(action.text);
    if (translation && !action.text_zh) {
      return { ...action, text_zh: translation };
    }
    return action;
  });
}

/**
 * Service for generating startup journey workflows
 * Handles persona detection and step generation based on user context
 */
export class JourneyWorkflowService {

  /**
   * Detects the most appropriate persona based on user context
   * Priority: founder-plan > expert-service > skilled-maker (default)
   */
  static detectPersona(context: PersonaDetectionContext): JourneyPersona {
    // Priority 1: founder-plan - Has a specific idea to validate
    if (context.hasSpecificIdea) {
      return 'founder-plan';
    }

    // Priority 2: expert-service - Has existing audience or runs service business
    if (context.hasAudience || context.serviceBusiness) {
      return 'expert-service';
    }

    // Priority 3 (Default): skilled-maker - Has technical skills, needs idea generation
    // This is the default fallback for all other cases
    return 'skilled-maker';
  }

  /**
   * Generates complete workflow including persona detection and step generation
   */
  static generateWorkflow(
    stage: JourneyStage,
    market: JourneyMarket,
    evaluation?: JourneyEvaluation
  ): { persona: JourneyPersona; steps: GeneratedStep[] } {
    // Create context for persona detection
    const context: PersonaDetectionContext = {
      stage,
      market,
      evaluation,
      // Infer flags from evaluation if available
      hasSpecificIdea: evaluation?.idea ? evaluation.idea >= 2 : false,
      hasAudience: market === 'Social Media/Influencer' || (evaluation?.network ? evaluation.network >= 2 : false),
      serviceBusiness: market === 'Other'
    };

    // Detect persona
    const persona = this.detectPersona(context);

    // Generate steps for the detected persona
    const steps = this.generateSteps(stage, market, persona);

    return { persona, steps };
  }

  /**
   * Generates journey steps based on stage, market, and persona
   */
  static generateSteps(
    stage: JourneyStage,
    market: JourneyMarket,
    persona: JourneyPersona
  ): GeneratedStep[] {
    // Get base steps for the stage
    let steps = this.getBaseStepsForStage(stage);

    // Apply persona-specific modifications
    steps = this.applyPersonaModifications(steps, persona, market, stage);

    return steps;
  }

  /**
   * Gets base workflow steps for each stage
   */
  private static getBaseStepsForStage(stage: JourneyStage): GeneratedStep[] {
    const baseStepsRaw: Record<JourneyStage, GeneratedStep[]> = {
      'Beginner': [
        {
          stepIndex: 0,
          title: 'Generate and Validate Ideas',
          title_zh: '生成和验证创意',
          description: 'Research demand and identify opportunities in your chosen market',
          description_zh: '研究需求并识别您所选市场中的机会',
          actions: [
            {
              text: 'Create Market Survey',
              text_zh: '创建市场调查',
              type: 'survey',
              pointsCost: -5,
              pointsReward: 15
            }
          ],
          dodCriteria: [
            '≥15-20 survey responses collected',
            'Top problems and opportunities identified'
          ],
          dodCriteria_zh: [
            '收集 ≥15-20 份调查回复',
            '识别首要问题和机会'
          ],
          timeEstimateWeeks: 1,
          status: 'current'
        },
        {
          stepIndex: 1,
          title: 'Build Your Solution',
          title_zh: '构建您的解决方案',
          description: 'Build a 1-Week MVP - Create a live, ultra-simple version of the tool',
          description_zh: '构建 1 周 MVP - 创建工具的实时超简单版本',
          actions: [
            {
              text: 'Step 1: Define your core action - Write exactly what your tool does in this format: "My tool will [input] -> [output]." For example: "Take a blog post URL -> Return a social media post" or "Take a photo -> Return outfit suggestions." Keep it simple and specific.',
              text_zh: '步骤 1：定义您的核心操作 - 用这种格式准确写出您的工具的功能："我的工具将 [输入] -> [输出]。" 例如："接收博客文章 URL -> 返回社交媒体帖子" 或 "接收照片 -> 返回服装建议。" 保持简单和具体。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
            },
            {
              text: 'Step 2: Choose your building approach - Pick either: (A) No-code tools like Bubble.io or Softr for visual building, or (B) Code + AI assistants like Claude, ChatGPT, or Cursor for faster development. Choose based on your comfort level.',
              text_zh: '步骤 2：选择您的构建方法 - 选择：(A) 无代码工具如 Bubble.io 或 Softr 进行可视化构建，或 (B) 代码 + AI 助手如 Claude、ChatGPT 或 Cursor 以加快开发速度。根据您的舒适度选择。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
            },
            {
              text: 'Step 3: Build ONLY the core feature - Focus solely on your main input->output function. Skip user accounts, fancy design, settings pages, or extra features. Make it work first, make it pretty later.',
              text_zh: '步骤 3：仅构建核心功能 - 专注于您的主要输入->输出功能。跳过用户账户、花哨的设计、设置页面或额外功能。先让它工作，再让它漂亮。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
            },
            {
              text: 'Step 4: Deploy online - Put your tool on the internet where people can actually use it. Use platforms like Vercel, Netlify, Heroku, or your no-code platform\'s hosting. Get a simple URL that works.',
              text_zh: '步骤 4：在线部署 - 将您的工具放到互联网上，让人们可以实际使用它。使用 Vercel、Netlify、Heroku 或您的无代码平台的托管服务。获取一个有效的简单 URL。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
            },
            {
              text: 'Step 5: Create a simple landing page - Make a basic webpage with: (1) A clear headline explaining what your tool does, (2) A GIF or video showing it working, (3) An email signup form to capture interested users. Keep it simple and focused.',
              text_zh: '步骤 5：创建简单的登陆页面 - 制作一个基本网页，包含：(1) 清晰的标题解释您的工具的功能，(2) 显示其工作原理的 GIF 或视频，(3) 电子邮件注册表单以捕获感兴趣的用户。保持简单和专注。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
            }
          ],
          dodCriteria: [
            'Create a live, ultra-simple version of the tool.'
          ],
          dodCriteria_zh: [
            '创建工具的实时超简单版本。'
          ],
          timeEstimateWeeks: 1,
          status: 'locked'
        },
        {
          stepIndex: 2,
          title: 'Test with Users',
          title_zh: '与用户测试',
          description: 'Get feedback from real potential customers using AI-generated pre-release project',
          description_zh: '使用 AI 生成的预发布项目从真实潜在客户那里获得反馈',
          type: 'pre-release',
          actions: [
            {
              text: 'Setup AI-Enhanced Pre-Release Project',
              text_zh: '设置 AI 增强的预发布项目',
              type: 'preRelease',
              pointsCost: -10,
              pointsReward: 25,
            }
          ],
          dodCriteria: [
            '≥20 beta users signed up',
            'User feedback collected and analyzed',
            'AI-generated pre-release assets deployed'
          ],
          dodCriteria_zh: [
            '≥20 名测试用户注册',
            '收集并分析用户反馈',
            '部署 AI 生成的预发布资料'
          ],
          timeEstimateWeeks: 2,
          status: 'locked'
        },
        {
          stepIndex: 3,
          title: 'Launch and Scale',
          title_zh: '发布和扩展',
          description: 'Generate launch assets and go public',
          description_zh: '生成发布资料并公开发布',
          actions: [
            {
              text: 'Generate Launch Assets',
              text_zh: '生成发布资料',
              type: 'launchAssets',
              pointsCost: -10,
              pointsReward: 30,
            }
          ],
          dodCriteria: [
            'Launch campaign materials generated',
            'Social media posts scheduled',
            'Email campaign ready',
            'Public launch completed',
            '≥100 users acquired'
          ],
          dodCriteria_zh: [
            '生成发布活动材料',
            '安排社交媒体帖子',
            '准备好电子邮件活动',
            '完成公开发布',
            '获得 ≥100 名用户'
          ],
          timeEstimateWeeks: 3,
          status: 'locked'
        }
      ],
      'Have Idea': [
        {
          stepIndex: 0,
          title: 'Rapid Idea Validation',
          title_zh: '快速创意验证',
          description: 'Test your riskiest assumptions quickly and cheaply. Validation prevents wasting months building something nobody wants.',
          description_zh: '快速且低成本地测试您最危险的假设。验证可以防止浪费数月时间构建无人需要的东西。',
          actions: [
            {
              text: '💡 WHY VALIDATION MATTERS: Validation saves you from building the wrong thing. 42% of startups fail because there\'s no market need. Spend 1 week validating now to avoid wasting 6 months building later.',
              text_zh: '💡 为什么验证很重要：验证可以避免您构建错误的东西。42% 的初创公司失败是因为没有市场需求。现在花 1 周时间验证，避免以后浪费 6 个月时间构建。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context'
            },
            {
              text: '📋 CRITICAL: Identify riskiest assumptions - List 5-7 assumptions your idea depends on (e.g., "People experience this problem weekly", "They would pay $X"), rank by risk (what would kill your idea if wrong?), select top 3 to test',
              text_zh: '📋 关键：识别最危险的假设 - 列出您的想法所依赖的 5-7 个假设（例如，"人们每周都会遇到这个问题"，"他们会支付 $X"），按风险排序（如果错误会扼杀您的想法？），选择前 3 个进行测试',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 5,
              priority: 'critical'
            },
            {
              text: '📊 CRITICAL: Create Validation Survey - Generate AI-powered survey with 8-10 questions testing your top assumptions',
              text_zh: '📊 关键：创建验证调查 - 生成包含 8-10 个问题的 AI 驱动调查，测试您的首要假设',
              type: 'survey',
              pointsCost: -5,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '📝 IMPORTANT: Design survey questions - Include: (1) Problem frequency: "How often do you experience [problem]?", (2) Current solution: "How do you solve this today?", (3) Interest level: "Would you use a solution that [your idea]?", (4) Price sensitivity: "What would you pay?", (5) Alternatives: "What else have you tried?"',
              text_zh: '📝 重要：设计调查问题 - 包括：(1) 问题频率："您多久遇到一次 [问题]？"，(2) 当前解决方案："您今天如何解决这个问题？"，(3) 兴趣程度："您会使用 [您的想法] 的解决方案吗？"，(4) 价格敏感度："您愿意支付多少？"，(5) 替代方案："您还尝试过什么？"',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 5,
              priority: 'important'
            },
            {
              text: '📢 CRITICAL: Share survey in 5+ channels - Post in: Reddit (r/[your niche]), Facebook groups, LinkedIn, Twitter/X, Discord servers, email to personal network. Aim for 20-30 responses minimum.',
              text_zh: '📢 关键：在 5+ 个渠道分享调查 - 发布在：Reddit (r/[您的领域])、Facebook 群组、LinkedIn、Twitter/X、Discord 服务器、向个人网络发送电子邮件。目标至少 20-30 个回复。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical'
            },
            {
              text: '📊 CRITICAL: Analyze results - Calculate: (1) Problem frequency: % who experience it weekly/daily, (2) Demand signal: % who said "definitely would use" (need ≥15%), (3) Price range: median and range of acceptable prices. ⚠️ RED FLAG: If <15% strong interest, pivot or refine idea before proceeding.',
              text_zh: '📊 关键：分析结果 - 计算：(1) 问题频率：每周/每天遇到的百分比，(2) 需求信号："肯定会使用"的百分比（需要 ≥15%），(3) 价格范围：可接受价格的中位数和范围。⚠️ 红旗：如果 <15% 强烈兴趣，在继续之前调整或改进想法。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical'
            },
            {
              text: '🔍 IMPORTANT: Research 5-10 competitors - For each, document: what they do well, customer complaints (check reviews), pricing model, target customer. Identify your differentiation angle.',
              text_zh: '🔍 重要：研究 5-10 个竞争对手 - 对于每个竞争对手，记录：他们做得好的地方、客户投诉（查看评论）、定价模式、目标客户。确定您的差异化角度。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            {
              text: '✅ OPTIONAL: If validation fails (<15% interest) - Use the pivot framework: (1) Change target customer, (2) Adjust solution approach, (3) Solve a different problem, or (4) Try a new idea. Don\'t proceed to building without validation.',
              text_zh: '✅ 可选：如果验证失败（<15% 兴趣）- 使用调整框架：(1) 更改目标客户，(2) 调整解决方案方法，(3) 解决不同的问题，或 (4) 尝试新想法。没有验证不要继续构建。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 5,
              priority: 'optional'
            }
          ],
          dodCriteria: [
            '≥20 validation survey responses collected',
            'Top 3 riskiest assumptions identified and tested',
            'Demand signal validated (≥15% strong interest)',
            'Price sensitivity assessed with target range',
            'Competitive alternatives researched'
          ],
          dodCriteria_zh: [
            '收集 ≥20 份验证调查回复',
            '识别并测试前 3 个最危险的假设',
            '验证需求信号（≥15% 强烈兴趣）',
            '评估价格敏感度并确定目标范围',
            '研究竞争替代方案'
          ],
          timeEstimateWeeks: 1,
          status: 'current'
        },
        {
          stepIndex: 1,
          title: 'Build MVP',
          title_zh: '构建 MVP',
          description: 'Create minimum viable product to test with real users. Focus on ONE core feature that proves your idea works.',
          description_zh: '创建最小可行产品以与真实用户测试。专注于证明您的想法有效的一个核心功能。',
          actions: [
            {
              text: '🎯 PHASE 1: SETUP (Days 1-3) - Define scope and prepare environment',
              text_zh: '🎯 阶段 1：设置（第 1-3 天）- 定义范围并准备环境',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'setup'
            },
            {
              text: '📝 CRITICAL: Define core action - Write "My tool will [INPUT] → [OUTPUT]" with specific examples. Example: "Take a blog post URL → Return 5 social media posts" or "Take a photo → Return outfit suggestions". Include acceptance criteria: what counts as "working"?',
              text_zh: '📝 关键：定义核心操作 - 用具体示例写出"我的工具将 [输入] → [输出]"。示例："接收博客文章 URL → 返回 5 个社交媒体帖子"或"接收照片 → 返回服装建议"。包括验收标准：什么算作"工作"？',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 5,
              priority: 'critical',
              phase: 'setup'
            },
            {
              text: '✂️ CRITICAL: List minimum features - Identify ONE core action. List what to SKIP for v1: user accounts, fancy design, settings pages, multiple features, advanced error handling. Make it work first, make it pretty later.',
              text_zh: '✂️ 关键：列出最小功能 - 确定一个核心操作。列出 v1 要跳过的内容：用户账户、花哨的设计、设置页面、多个功能、高级错误处理。先让它工作，再让它漂亮。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 5,
              priority: 'critical',
              phase: 'setup'
            },
            {
              text: '🛠️ IMPORTANT: Choose tech stack - Take the quiz to get personalized recommendation. Options: (A) No-code: Bubble.io, Softr, Glide (faster, less flexible), (B) Code + AI: React/Next.js + Claude/ChatGPT (more control, steeper learning). Choose based on: skill level, budget, timeline, custom logic needs.',
              text_zh: '🛠️ 重要：选择技术栈 - 参加测验以获得个性化推荐。选项：(A) 无代码：Bubble.io、Softr、Glide（更快，灵活性较低），(B) 代码 + AI：React/Next.js + Claude/ChatGPT（更多控制，学习曲线更陡）。根据以下选择：技能水平、预算、时间表、自定义逻辑需求。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 5,
              priority: 'important',
              phase: 'setup'
            },
            {
              text: '⚙️ IMPORTANT: Set up environment - Create new project, install dependencies, test "Hello World" deployment to verify setup works. Use starter templates to save time.',
              text_zh: '⚙️ 重要：设置环境 - 创建新项目，安装依赖项，测试"Hello World"部署以验证设置有效。使用启动模板以节省时间。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 5,
              priority: 'important',
              phase: 'setup'
            },
            {
              text: '🏗️ PHASE 2: BUILD (Days 4-12) - Implement core feature only',
              text_zh: '🏗️ 阶段 2：构建（第 4-12 天）- 仅实现核心功能',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'build'
            },
            {
              text: '📥 CRITICAL: Build input mechanism - Create form/upload for user input (Days 4-6). Keep it simple: basic form fields or file upload. Test that data is captured correctly.',
              text_zh: '📥 关键：构建输入机制 - 为用户输入创建表单/上传（第 4-6 天）。保持简单：基本表单字段或文件上传。测试数据是否正确捕获。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'build'
            },
            {
              text: '⚙️ CRITICAL: Build processing logic - Implement core functionality (Days 7-9). This is your main value: the transformation from input to output. Use AI assistants (Claude, ChatGPT) to write code faster. Focus on making it work, not perfect.',
              text_zh: '⚙️ 关键：构建处理逻辑 - 实现核心功能（第 7-9 天）。这是您的主要价值：从输入到输出的转换。使用 AI 助手（Claude、ChatGPT）更快地编写代码。专注于让它工作，而不是完美。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical',
              phase: 'build'
            },
            {
              text: '📤 CRITICAL: Build output display - Show results to user (Days 10-11). Simple is fine: basic text, list, or table. Make sure users can see and understand the output.',
              text_zh: '📤 关键：构建输出显示 - 向用户显示结果（第 10-11 天）。简单就好：基本文本、列表或表格。确保用户可以看到并理解输出。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'build'
            },
            {
              text: '🔗 CRITICAL: Connect end-to-end - Test complete input → processing → output flow (Day 12). Verify the entire journey works without errors. This is your "it works!" moment. 🎉',
              text_zh: '🔗 关键：连接端到端 - 测试完整的输入 → 处理 → 输出流程（第 12 天）。验证整个过程无错误运行。这是您的"它工作了！"时刻。🎉',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical',
              phase: 'build'
            },
            {
              text: '🚀 PHASE 3: DEPLOY (Days 13-21) - Make it accessible and trackable',
              text_zh: '🚀 阶段 3：部署（第 13-21 天）- 使其可访问和可跟踪',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'deploy'
            },
            {
              text: '🌐 CRITICAL: Deploy online - Put your MVP on the internet (Days 13-14). Use: Vercel (best for Next.js/React), Netlify (great for static sites), Heroku/Railway (good for backend apps), or your no-code platform\'s hosting. Get a public URL that works.',
              text_zh: '🌐 关键：在线部署 - 将您的 MVP 放到互联网上（第 13-14 天）。使用：Vercel（最适合 Next.js/React）、Netlify（适合静态网站）、Heroku/Railway（适合后端应用）或您的无代码平台的托管。获取有效的公共 URL。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 20,
              priority: 'critical',
              phase: 'deploy'
            },
            {
              text: '✅ CRITICAL: Test deployment - Verify anyone with the link can use it (Day 15). Open in incognito mode, test on different browsers, share with a friend. Make sure it actually works for others.',
              text_zh: '✅ 关键：测试部署 - 验证任何有链接的人都可以使用它（第 15 天）。在隐身模式下打开，在不同浏览器上测试，与朋友分享。确保它对其他人真正有效。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'deploy'
            },
            {
              text: '📄 CRITICAL: Create landing page - Make a simple page (Days 16-17) with: (1) Clear headline: "What problem does this solve?", (2) Demo: GIF/video showing it working (use Loom + GIF converter), (3) Email signup form (Mailchimp, ConvertKit, or Google Form), (4) "Try It Now" button.',
              text_zh: '📄 关键：创建登陆页面 - 制作一个简单的页面（第 16-17 天），包含：(1) 清晰的标题："这解决了什么问题？"，(2) 演示：显示其工作原理的 GIF/视频（使用 Loom + GIF 转换器），(3) 电子邮件注册表单（Mailchimp、ConvertKit 或 Google 表单），(4)"立即试用"按钮。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical',
              phase: 'deploy'
            },
            {
              text: '📱 IMPORTANT: Make mobile-responsive - Test on desktop, mobile, tablet (Day 18). Most users will view on mobile. Use responsive design or mobile-first frameworks.',
              text_zh: '📱 重要：使其适应移动设备 - 在桌面、移动设备、平板电脑上测试（第 18 天）。大多数用户将在移动设备上查看。使用响应式设计或移动优先框架。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 5,
              priority: 'important',
              phase: 'deploy'
            },
            {
              text: '📊 IMPORTANT: Set up analytics - Add Google Analytics or Plausible (Day 19). Track: page views, button clicks, email signups, tool usage. Test that events fire correctly.',
              text_zh: '📊 重要：设置分析 - 添加 Google Analytics 或 Plausible（第 19 天）。跟踪：页面浏览量、按钮点击、电子邮件注册、工具使用情况。测试事件是否正确触发。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important',
              phase: 'deploy'
            },
            {
              text: '🧪 CRITICAL: End-to-end testing - Test complete user journey (Days 20-21): land on page → watch demo → sign up → use tool. Get 3-5 friends to test and give feedback. Fix critical issues only.',
              text_zh: '🧪 关键：端到端测试 - 测试完整的用户旅程（第 20-21 天）：登陆页面 → 观看演示 → 注册 → 使用工具。让 3-5 个朋友测试并提供反馈。仅修复关键问题。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical',
              phase: 'deploy'
            }
          ],
          dodCriteria: [
            'Core feature deployed and accessible online',
            'Landing page with demo/GIF created',
            'Email signup form integrated',
            'Basic analytics tracking implemented',
            'End-to-end user flow tested and working'
          ],
          dodCriteria_zh: [
            '核心功能已部署并可在线访问',
            '创建带有演示/GIF 的登陆页面',
            '集成电子邮件注册表单',
            '实施基本分析跟踪',
            '端到端用户流程已测试并正常工作'
          ],
          timeEstimateWeeks: 3,
          status: 'locked'
        },
        {
          stepIndex: 2,
          title: 'Get Early Users',
          title_zh: '获取早期用户',
          description: 'Acquire and learn from your first 50 customers. Focus on quality feedback over quantity.',
          description_zh: '获取并学习您的前 50 个客户。注重质量反馈而非数量。',
          actions: [
            {
              text: '🚀 PHASE 1: SETUP BETA (Days 1-3) - Prepare beta infrastructure and launch materials',
              text_zh: '🚀 阶段 1：设置测试版（第 1-3 天）- 准备测试版基础设施和发布材料',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'setup'
            },
            {
              text: '🚀 CRITICAL: Generate Beta Program Assets - Auto-creates: beta landing page, welcome email sequence, feedback survey, and analytics tracking. This sets up your entire beta infrastructure.',
              text_zh: '🚀 关键：生成测试版程序资料 - 自动创建：测试版登陆页面、欢迎电子邮件序列、反馈调查和分析跟踪。这将设置您的整个测试版基础设施。',
              type: 'preRelease',
              pointsCost: -10,
              pointsReward: 25,
              priority: 'critical',
              phase: 'setup'
            },
            {
              text: '📄 IMPORTANT: Customize beta landing page - Edit the generated page with: "Join Our Beta" headline, benefits (early access, influence product, priority support), expectations (bugs expected, feedback needed), signup form. Target: 50-100 signups.',
              text_zh: '📄 重要：自定义测试版登陆页面 - 编辑生成的页面，包含："加入我们的测试版"标题、好处（早期访问、影响产品、优先支持）、期望（预期有错误、需要反馈）、注册表单。目标：50-100 个注册。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important',
              phase: 'setup'
            },
            {
              text: '📧 IMPORTANT: Set up email automation - Configure: (1) Welcome email (sent within 1 hour), (2) Onboarding sequence (3-5 emails over first week), (3) Feedback request (after 1 week). Use Mailchimp, ConvertKit, or generated templates.',
              text_zh: '📧 重要：设置电子邮件自动化 - 配置：(1) 欢迎电子邮件（1 小时内发送），(2) 入职序列（第一周内 3-5 封电子邮件），(3) 反馈请求（1 周后）。使用 Mailchimp、ConvertKit 或生成的模板。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important',
              phase: 'setup'
            },
            {
              text: '👥 PHASE 2: LAUNCH (Days 4-10) - Recruit beta users and onboard them',
              text_zh: '👥 阶段 2：发布（第 4-10 天）- 招募测试版用户并引导他们',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'launch'
            },
            {
              text: '👥 CRITICAL: Launch to your network - Email 50-100 personal contacts, post on LinkedIn/Twitter/Facebook. Personal outreach converts 10-20% vs 1-2% for cold outreach. Start with warm audience first.',
              text_zh: '👥 关键：向您的网络发布 - 向 50-100 个个人联系人发送电子邮件，在 LinkedIn/Twitter/Facebook 上发布。个人外展转化率为 10-20%，而冷外展为 1-2%。首先从温暖的受众开始。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'launch'
            },
            {
              text: '🌐 CRITICAL: Post in online communities - Share in: Reddit (r/[your niche], r/SideProject), Product Hunt Ship, Indie Hackers, Hacker News "Show HN", Facebook groups, Discord servers. Be helpful, not spammy. Aim for 20-30 signups from communities.',
              text_zh: '🌐 关键：在在线社区发布 - 分享到：Reddit (r/[您的领域]、r/SideProject)、Product Hunt Ship、Indie Hackers、Hacker News "Show HN"、Facebook 群组、Discord 服务器。要有帮助，不要发垃圾邮件。目标从社区获得 20-30 个注册。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical',
              phase: 'launch'
            },
            {
              text: '✅ CRITICAL: Onboard users personally - Send welcome email within 1 hour of signup. Include: (1) Quick start guide (3-5 steps), (2) Link to demo video, (3) Your email for questions. Manually onboard first 20 users for best feedback.',
              text_zh: '✅ 关键：亲自引导用户 - 在注册后 1 小时内发送欢迎电子邮件。包括：(1) 快速入门指南（3-5 步），(2) 演示视频链接，(3) 您的电子邮件以供提问。手动引导前 20 个用户以获得最佳反馈。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'launch'
            },
            {
              text: '📊 IMPORTANT: Monitor activation rate - Track % who complete first action. Goal: ≥50% activation. If <30%, reach out to inactive users within 48 hours: "What\'s blocking you?" Fix onboarding issues immediately.',
              text_zh: '📊 重要：监控激活率 - 跟踪完成第一个操作的百分比。目标：≥50% 激活。如果 <30%，在 48 小时内联系不活跃的用户："什么阻碍了您？"立即修复入职问题。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important',
              phase: 'launch'
            },
            {
              text: '📊 PHASE 3: ANALYZE (Days 11-14) - Collect feedback and optimize',
              text_zh: '📊 阶段 3：分析（第 11-14 天）- 收集反馈并优化',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'analyze'
            },
            {
              text: '🎤 CRITICAL: Conduct 10+ user interviews - Schedule 15-30 min calls. Ask: (1) "What problem were you trying to solve?", (2) "How well did our tool solve it?" (1-10), (3) "What frustrated you most?", (4) "What would make you use this daily?", (5) "Would you recommend to others?" (NPS). Record and take notes.',
              text_zh: '🎤 关键：进行 10+ 次用户访谈 - 安排 15-30 分钟的通话。询问：(1)"您试图解决什么问题？"，(2)"我们的工具解决得如何？"（1-10），(3)"什么最让您沮丧？"，(4)"什么会让您每天使用它？"，(5)"您会向其他人推荐吗？"（NPS）。录音并做笔记。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 20,
              priority: 'critical',
              phase: 'analyze'
            },
            {
              text: '📋 IMPORTANT: Send feedback survey - Use Typeform or Google Forms. Include: (1) Satisfaction rating, (2) NPS question: "How likely to recommend?" (0-10), (3) Feature requests, (4) Biggest frustration. Keep it short (5-7 questions).',
              text_zh: '📋 重要：发送反馈调查 - 使用 Typeform 或 Google 表单。包括：(1) 满意度评分，(2) NPS 问题："推荐的可能性有多大？"（0-10），(3) 功能请求，(4) 最大的挫折。保持简短（5-7 个问题）。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important',
              phase: 'analyze'
            },
            {
              text: '📈 CRITICAL: Calculate key metrics - Track: (1) Total signups: [number], (2) Activated users: [number] (≥50 required), (3) Week 1 retention: [%], (4) Week 2 retention: [%] (≥30% required), (5) NPS: [score] (≥40 target). Use analytics dashboard or spreadsheet.',
              text_zh: '📈 关键：计算关键指标 - 跟踪：(1) 总注册数：[数字]，(2) 激活用户：[数字]（需要 ≥50），(3) 第 1 周留存：[%]，(4) 第 2 周留存：[%]（需要 ≥30%），(5) NPS：[分数]（目标 ≥40）。使用分析仪表板或电子表格。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical',
              phase: 'analyze'
            },
            {
              text: '🔍 CRITICAL: Analyze feedback patterns - List: (1) Top 5 complaints, (2) Top 5 feature requests, (3) Common user behaviors. Look for patterns: if 3+ users mention same issue, it\'s critical. Categorize feedback: bugs, features, UX issues.',
              text_zh: '🔍 关键：分析反馈模式 - 列出：(1) 前 5 个投诉，(2) 前 5 个功能请求，(3) 常见用户行为。寻找模式：如果 3+ 个用户提到相同问题，则为关键。对反馈进行分类：错误、功能、UX 问题。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'analyze'
            },
            {
              text: '🎯 CRITICAL: Prioritize improvements - Create 3 lists: (1) Critical bugs (fix immediately), (2) High-impact features (add next sprint), (3) Nice-to-haves (backlog). Focus on what 80% of users need, not edge cases. Ship improvements weekly.',
              text_zh: '🎯 关键：优先改进 - 创建 3 个列表：(1) 关键错误（立即修复），(2) 高影响功能（下一个冲刺添加），(3) 锦上添花（待办事项）。专注于 80% 用户需要的内容，而不是边缘情况。每周发布改进。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'analyze'
            }
          ],
          dodCriteria: [
            '≥50 early users signed up and activated',
            '≥10 user feedback interviews completed',
            'User retention rate measured (≥30% week 2)',
            'Net Promoter Score collected (target ≥40)',
            'Top feature requests documented and prioritized'
          ],
          dodCriteria_zh: [
            '≥50 名早期用户注册并激活',
            '完成 ≥10 次用户反馈访谈',
            '测量用户留存率（第 2 周 ≥30%）',
            '收集净推荐值（目标 ≥40）',
            '记录并优先排序首要功能请求'
          ],
          timeEstimateWeeks: 2,
          status: 'locked'
        },
        {
          stepIndex: 3,
          title: 'Scale and Grow',
          title_zh: '扩展和增长',
          description: 'Reach 200+ users by optimizing your funnel and doubling down on what works. Focus beats spreading thin.',
          description_zh: '通过优化您的渠道并加倍投入有效的方法来达到 200+ 用户。专注胜过分散精力。',
          actions: [
            {
              text: '🎯 WEEK 1: IDENTIFY & OPTIMIZE - Find your best channels and fix conversion',
              text_zh: '🎯 第 1 周：识别和优化 - 找到您最好的渠道并修复转化',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'week1'
            },
            {
              text: '📊 CRITICAL: Review acquisition data - Analyze Step 2 results: which channels brought most users? Which had highest activation rate? Which had lowest cost per user? Rank channels by: (1) Total signups, (2) Activation %, (3) Cost/time investment.',
              text_zh: '📊 关键：审查获取数据 - 分析步骤 2 结果：哪些渠道带来了最多用户？哪个激活率最高？哪个每用户成本最低？按以下排序渠道：(1) 总注册数，(2) 激活百分比，(3) 成本/时间投资。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'week1'
            },
            {
              text: '🧪 IMPORTANT: Test 2-3 new channels (Week 1) - Pick from: Content marketing (blog/SEO), Social media (Twitter/LinkedIn/TikTok), Communities (Reddit/forums), Paid ads (Google/Facebook), Product Hunt, Partnerships. Run 1-week test for each. Track signups and cost.',
              text_zh: '🧪 重要：测试 2-3 个新渠道（第 1 周）- 从以下选择：内容营销（博客/SEO）、社交媒体（Twitter/LinkedIn/TikTok）、社区（Reddit/论坛）、付费广告（Google/Facebook）、Product Hunt、合作伙伴关系。对每个进行 1 周测试。跟踪注册和成本。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important',
              phase: 'week1'
            },
            {
              text: '✅ CRITICAL: Select top 3 channels - Based on data, choose 3 channels to focus on. Criteria: (1) Signups per week, (2) Cost per signup, (3) Activation rate. Ignore vanity metrics. Double down on winners, cut losers.',
              text_zh: '✅ 关键：选择前 3 个渠道 - 根据数据，选择 3 个要关注的渠道。标准：(1) 每周注册数，(2) 每次注册成本，(3) 激活率。忽略虚荣指标。加倍投入赢家，削减输家。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'week1'
            },
            {
              text: '🔍 CRITICAL: Map conversion funnel - Document: Visitor → Signup → Activation → Retention. Measure conversion at each stage. Find biggest drop-off point (usually signup → activation). This is where to optimize first.',
              text_zh: '🔍 关键：绘制转化漏斗 - 记录：访客 → 注册 → 激活 → 留存。测量每个阶段的转化。找到最大的流失点（通常是注册 → 激活）。这是首先要优化的地方。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'week1'
            },
            {
              text: '🧪 IMPORTANT: Run A/B tests - Test one thing at a time: (1) Landing page headline (3 versions), (2) CTA button text/color, (3) Signup form (reduce fields), (4) Onboarding flow (simplify steps). Use Google Optimize or Optimizely. Run each test for 100+ visitors.',
              text_zh: '🧪 重要：运行 A/B 测试 - 一次测试一件事：(1) 登陆页面标题（3 个版本），(2) CTA 按钮文本/颜色，(3) 注册表单（减少字段），(4) 入职流程（简化步骤）。使用 Google Optimize 或 Optimizely。对 100+ 访客运行每个测试。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important',
              phase: 'week1'
            },
            {
              text: '📈 CRITICAL: Optimize to ≥20% conversion - Goal: 20% of signups become active users. If below 20%: (1) Simplify onboarding, (2) Add onboarding emails, (3) Reduce friction, (4) Improve first-time experience. Measure weekly progress.',
              text_zh: '📈 关键：优化至 ≥20% 转化 - 目标：20% 的注册成为活跃用户。如果低于 20%：(1) 简化入职，(2) 添加入职电子邮件，(3) 减少摩擦，(4) 改善首次体验。每周测量进度。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 20,
              priority: 'critical',
              phase: 'week1'
            },
            {
              text: '📅 WEEK 2-3: CONTENT & VIRALITY - Build consistent presence and word-of-mouth',
              text_zh: '📅 第 2-3 周：内容和病毒式传播 - 建立持续存在和口碑',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'week2'
            },
            {
              text: '📝 IMPORTANT: Create 30-day content calendar - Plan: (1) 20 social posts (Twitter/LinkedIn), (2) 4 blog posts (SEO-focused), (3) 2-3 tutorial videos. Focus on: how-tos, use cases, customer stories. Use AI tools (ChatGPT, Jasper) to speed up creation.',
              text_zh: '📝 重要：创建 30 天内容日历 - 计划：(1) 20 个社交帖子（Twitter/LinkedIn），(2) 4 篇博客文章（SEO 重点），(3) 2-3 个教程视频。专注于：操作指南、用例、客户故事。使用 AI 工具（ChatGPT、Jasper）加快创建速度。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important',
              phase: 'week2'
            },
            {
              text: '⏰ IMPORTANT: Schedule content - Use Buffer, Hootsuite, or Later. Post consistently: daily or 3x/week. Best times: weekdays 9am-5pm. Engage with comments within 1 hour. Consistency > perfection.',
              text_zh: '⏰ 重要：安排内容 - 使用 Buffer、Hootsuite 或 Later。持续发布：每天或每周 3 次。最佳时间：工作日上午 9 点至下午 5 点。在 1 小时内回复评论。一致性 > 完美。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important',
              phase: 'week2'
            },
            {
              text: '🎁 IMPORTANT: Design referral program - Define: (1) Referrer reward: free month, credits, or premium features, (2) Referred user incentive: discount or bonus credits. Keep it simple. Example: "Give $10, Get $10".',
              text_zh: '🎁 重要：设计推荐计划 - 定义：(1) 推荐人奖励：免费月份、积分或高级功能，(2) 被推荐用户激励：折扣或奖励积分。保持简单。示例："给 $10，得 $10"。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important',
              phase: 'week2'
            },
            {
              text: '🔗 OPTIONAL: Build referral mechanism - Use tools: ReferralCandy, Viral Loops, or GrowSurf (easier than custom). Or build custom: generate unique links, track referrals, automate rewards. Test with 10 users before full launch.',
              text_zh: '🔗 可选：构建推荐机制 - 使用工具：ReferralCandy、Viral Loops 或 GrowSurf（比自定义更容易）。或构建自定义：生成唯一链接、跟踪推荐、自动化奖励。在全面发布前与 10 个用户测试。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 20,
              priority: 'optional',
              phase: 'week2'
            },
            {
              text: '🚀 IMPORTANT: Add viral elements - Make sharing easy: (1) "Share your results" button, (2) "Made with [Your Tool]" watermark, (3) Social sharing prompts after success, (4) "Invite team members" feature. Goal: ≥0.5 viral coefficient (each user brings 0.5 more).',
              text_zh: '🚀 重要：添加病毒式元素 - 使分享变得容易：(1)"分享您的结果"按钮，(2)"使用 [您的工具] 制作"水印，(3) 成功后的社交分享提示，(4)"邀请团队成员"功能。目标：≥0.5 病毒系数（每个用户带来 0.5 个更多用户）。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important',
              phase: 'week2'
            },
            {
              text: '🏆 WEEK 4: BIG LAUNCHES - Product Hunt and paid acquisition',
              text_zh: '🏆 第 4 周：大型发布 - Product Hunt 和付费获取',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'week4'
            },
            {
              text: '🚀 IMPORTANT: Launch on Product Hunt - Prepare: (1) Logo, screenshots, demo video, (2) Compelling description (problem → solution → benefits), (3) Rally 20+ supporters to upvote at 12:01am PT, (4) Engage with every comment all day. Goal: Top 5 product of the day. Expect 100-500 signups.',
              text_zh: '🚀 重要：在 Product Hunt 上发布 - 准备：(1) Logo、截图、演示视频，(2) 引人注目的描述（问题 → 解决方案 → 好处），(3) 召集 20+ 支持者在太平洋时间凌晨 12:01 点赞，(4) 全天回复每条评论。目标：当天前 5 名产品。预期 100-500 个注册。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 25,
              priority: 'important',
              phase: 'week4'
            },
            {
              text: '💰 OPTIONAL: Run paid campaigns - Start small: $500-1000 budget. Test: Facebook/Instagram ads, Google Ads, or Twitter ads. Target: <$10 cost per signup. If >$10, pause and optimize. Scale winning campaigns 2x per week. Track ROI closely.',
              text_zh: '💰 可选：运行付费活动 - 从小开始：$500-1000 预算。测试：Facebook/Instagram 广告、Google 广告或 Twitter 广告。目标：每次注册成本 <$10。如果 >$10，暂停并优化。每周将获胜活动扩大 2 倍。密切跟踪 ROI。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 20,
              priority: 'optional',
              phase: 'week4'
            },
            {
              text: '🤝 OPTIONAL: Build partnerships - Reach out to 10 complementary tools (not competitors). Propose: (1) Integration (embed each other), (2) Co-marketing (joint webinar/content), (3) Affiliate program. Focus on tools your users already use. Expect 10-20% response rate.',
              text_zh: '🤝 可选：建立合作伙伴关系 - 联系 10 个互补工具（非竞争对手）。提议：(1) 集成（相互嵌入），(2) 联合营销（联合网络研讨会/内容），(3) 联盟计划。专注于您的用户已经使用的工具。预期 10-20% 响应率。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'optional',
              phase: 'week4'
            },
            {
              text: '📊 CRITICAL: Track growth metrics - Monitor weekly: (1) Signups: [number], (2) Total active users: [number] (≥200 goal), (3) Week-over-week growth: [%] (≥15% goal), (4) Top 3 channels performance. Use dashboard or spreadsheet. ✅ GRADUATION: Once you hit 200 users + 15% monthly growth, you\'re ready for next stage!',
              text_zh: '📊 关键：跟踪增长指标 - 每周监控：(1) 注册数：[数字]，(2) 总活跃用户：[数字]（≥200 目标），(3) 周环比增长：[%]（≥15% 目标），(4) 前 3 个渠道表现。使用仪表板或电子表格。✅ 毕业：一旦达到 200 个用户 + 15% 月增长，您就准备好进入下一阶段了！',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical',
              phase: 'week4'
            }
          ],
          dodCriteria: [
            'Top 3 growth channels identified and tested',
            'Conversion funnel optimized (≥20% signup-to-active)',
            'Content calendar created for 30 days',
            'Referral or viral loop implemented',
            '≥200 active users achieved',
            'Monthly growth rate ≥15% established'
          ],
          dodCriteria_zh: [
            '识别并测试前 3 个增长渠道',
            '优化转化漏斗（注册到激活 ≥20%）',
            '创建 30 天内容日历',
            '实施推荐或病毒式传播循环',
            '达到 ≥200 名活跃用户',
            '建立月增长率 ≥15%'
          ],
          timeEstimateWeeks: 4,
          status: 'locked'
        }
      ],
      'In Progress': [
        {
          stepIndex: 0,
          title: 'Optimize Product-Market Fit',
          title_zh: '优化产品市场契合度',
          description: 'Use data and feedback to identify top product improvements',
          description_zh: '使用数据和反馈识别首要产品改进',
          actions: [
            {
              text: '💡 WHY PRODUCT OPTIMIZATION MATTERS: 70% of startups that achieve product-market fit do so through systematic iteration based on user data. At 200+ users, you have enough data to make informed decisions. Don\'t guess what to build next—let your users and data guide you.',
              text_zh: '💡 为什么产品优化很重要：70% 达到产品市场契合度的初创公司是通过基于用户数据的系统化迭代实现的。在 200+ 用户时，您有足够的数据做出明智的决策。不要猜测下一步要构建什么——让您的用户和数据指导您。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context'
            },
            {
              text: '📊 CRITICAL: Analyze Key Metrics - Review your product analytics to understand user behavior. Track: (1) Activation rate: % of signups who complete first key action (target ≥40%), (2) Retention cohorts: % of users active after week 2, week 4 (target ≥30% week-2), (3) Feature usage: which features are used most/least, (4) Churn rate: % of users who stop using your product (target <5% monthly). Tools: Google Analytics, Mixpanel, Amplitude, or custom dashboard. Create a spreadsheet with current metrics vs targets.',
              text_zh: '📊 关键：分析关键指标 - 查看您的产品分析以了解用户行为。跟踪：(1) 激活率：完成第一个关键操作的注册百分比（目标 ≥40%），(2) 留存队列：第 2 周、第 4 周后活跃的用户百分比（目标第 2 周 ≥30%），(3) 功能使用：哪些功能使用最多/最少，(4) 流失率：停止使用您产品的用户百分比（目标每月 <5%）。工具：Google Analytics、Mixpanel、Amplitude 或自定义仪表板。创建一个包含当前指标与目标的电子表格。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical'
            },
            {
              text: '🎤 CRITICAL: Conduct User Interviews - Schedule 10-15 interviews with active users to understand their experience. Ask: (1) "How satisfied are you with [product]?" (1-10 scale), (2) "What are your biggest pain points or frustrations?", (3) "What features would make you use this daily?", (4) "What alternatives have you tried?", (5) "Would you recommend to others?" (NPS). Record interviews using Loom, Zoom, or phone. Take detailed notes and identify patterns across interviews.',
              text_zh: '🎤 关键：进行用户访谈 - 安排与 10-15 名活跃用户的访谈以了解他们的体验。询问：(1) "您对 [产品] 的满意度如何？"（1-10 分），(2) "您最大的痛点或挫折是什么？"，(3) "什么功能会让您每天使用它？"，(4) "您尝试过哪些替代方案？"，(5) "您会向其他人推荐吗？"（NPS）。使用 Loom、Zoom 或电话录制访谈。做详细笔记并识别访谈中的模式。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '🔍 IMPORTANT: Analyze Behavioral Data - Review user flows to identify where users drop off or get stuck. Questions to answer: (1) Where do users abandon the flow? (2) What do power users do differently? (3) Which features correlate with retention? (4) What actions predict churn? Tools: Hotjar (heatmaps, session recordings), FullStory (session replay), or custom analytics. Document top 3 friction points with evidence.',
              text_zh: '🔍 重要：分析行为数据 - 查看用户流程以识别用户在哪里流失或卡住。要回答的问题：(1) 用户在哪里放弃流程？(2) 高级用户有什么不同？(3) 哪些功能与留存相关？(4) 哪些操作预测流失？工具：Hotjar（热图、会话录制）、FullStory（会话重放）或自定义分析。记录前 3 个摩擦点并提供证据。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            {
              text: '🎯 CRITICAL: Prioritize Improvements - Use an Impact vs Effort matrix to rank potential improvements. For each improvement: (1) User demand: How many users mentioned this? (High/Medium/Low), (2) Impact on retention: Will this improve key metrics? (High/Medium/Low), (3) Development effort: How long to build? (1 week / 2 weeks / 1 month). Plot on matrix: High Impact + Low Effort = Do First. Select top 5 improvements and rank them. Document rationale for each.',
              text_zh: '🎯 关键：优先排序改进 - 使用影响与努力矩阵对潜在改进进行排名。对于每个改进：(1) 用户需求：有多少用户提到这个？（高/中/低），(2) 对留存的影响：这会改善关键指标吗？（高/中/低），(3) 开发工作量：构建需要多长时间？（1 周 / 2 周 / 1 个月）。在矩阵上绘制：高影响 + 低努力 = 首先做。选择前 5 个改进并对其进行排名。记录每个的理由。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical'
            },
            {
              text: '📋 IMPORTANT: Create Implementation Tasks - Break your top 3 improvements into actionable 1-2 week tasks. For each improvement: (1) Define acceptance criteria: What does "done" look like? (2) Break into subtasks: Design, development, testing, (3) Estimate time: Be realistic, (4) Assign owner: Who will do this? Create tasks in your project management tool or use SeedDots task creation feature. Link tasks back to this journey step.',
              text_zh: '📋 重要：创建实施任务 - 将您的前 3 个改进分解为可操作的 1-2 周任务。对于每个改进：(1) 定义验收标准："完成"是什么样子？(2) 分解为子任务：设计、开发、测试，(3) 估计时间：要现实，(4) 分配所有者：谁来做这个？在您的项目管理工具中创建任务或使用 SeedDots 任务创建功能。将任务链接回此旅程步骤。',
              type: 'task',
              pointsCost: -5,
              pointsReward: 15,
              priority: 'important'
            },
            {
              text: '✅ View Definition of Done - See exactly what you need to complete for this step',
              text_zh: '✅ 查看完成定义 - 准确了解您需要为此步骤完成什么',
              type: 'definitionOfDone',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'critical'
            }
          ],
          dodCriteria: [
            '≥10 user interviews completed with detailed notes',
            'Key metrics analyzed: activation rate, retention cohorts, feature usage, churn rate',
            'Behavioral data analyzed with top 3 friction points identified',
            'Top 5 product improvements identified and prioritized using Impact vs Effort matrix',
            'Top 3 improvements broken into implementation tasks'
          ],
          dodCriteria_zh: [
            '完成 ≥10 次用户访谈并做详细笔记',
            '分析关键指标：激活率、留存队列、功能使用、流失率',
            '分析行为数据并识别前 3 个摩擦点',
            '使用影响与努力矩阵识别并优先排序前 5 个产品改进',
            '将前 3 个改进分解为实施任务'
          ],
          timeEstimateWeeks: 2,
          status: 'current'
        },
        {
          stepIndex: 1,
          title: 'Build Your Team',
          title_zh: '组建您的团队',
          description: 'Recruit key team members to scale',
          description_zh: '招募关键团队成员以扩展规模',
          actions: [
            {
              text: 'Post Team Request',
              type: 'team',
              pointsCost: -8,
              pointsReward: 20,
              priority: 'critical'
            }
          ],
          dodCriteria: [
            'Key roles identified',
            '≥2 team members recruited'
          ],
          dodCriteria_zh: [
            '识别关键角色',
            '招募 ≥2 名团队成员'
          ],
          timeEstimateWeeks: 3,
          status: 'locked'
        },
        {
          stepIndex: 2,
          title: 'Expand Market Reach',
          title_zh: '扩大市场覆盖',
          description: 'Scale from 200 to 500+ users through systematic growth tactics',
          description_zh: '通过系统化增长策略从 200 扩展到 500+ 用户',
          actions: [
            // Context action
            {
              text: '💡 WHY SYSTEMATIC GROWTH MATTERS: Random marketing tactics waste time and money. Focused growth on 2-3 channels outperforms spreading thin by 5x. At 200+ users, you have enough data to identify what\'s working. This 4-week plan helps you double down on winners and systematically scale to 500+ users.',
              text_zh: '💡 为什么系统化增长很重要：随机营销策略浪费时间和金钱。专注于 2-3 个渠道的增长比分散精力的效果好 5 倍。在 200+ 用户时，您有足够的数据来识别什么有效。这个 4 周计划帮助您加倍投入成功的渠道，系统化地扩展到 500+ 用户。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context'
            },

            // WEEK 1: AUDIT & OPTIMIZE
            {
              text: '🎯 WEEK 1: AUDIT & OPTIMIZE - Identify your best channels and optimize conversion',
              text_zh: '🎯 第 1 周：审计与优化 - 识别您最好的渠道并优化转化',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'week1'
            },
            {
              text: '📊 CRITICAL: Audit Current Channels - Review where your 200 users came from. Create a spreadsheet tracking: (1) Channel name (organic search, social media, referrals, etc.), (2) Signups per channel, (3) Activation rate per channel (% who complete first action), (4) Cost per user (if paid). Calculate which channels have: highest volume, best activation rate, lowest cost. Tools: Google Analytics (Acquisition reports), your signup database, payment records. Rank channels by quality score: (signups × activation rate) / cost.',
              text_zh: '📊 关键：审计当前渠道 - 查看您的 200 个用户来自哪里。创建一个电子表格跟踪：(1) 渠道名称（自然搜索、社交媒体、推荐等），(2) 每个渠道的注册数，(3) 每个渠道的激活率（完成第一个操作的百分比），(4) 每个用户的成本（如果是付费的）。计算哪些渠道具有：最高数量、最佳激活率、最低成本。工具：Google Analytics（获取报告）、您的注册数据库、付款记录。按质量分数对渠道进行排名：（注册数 × 激活率）/ 成本。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'week1'
            },
            {
              text: '🎯 CRITICAL: Identify Top 3 Channels - Based on your audit, select 3 channels to focus on. Criteria: (1) Proven performance: already bringing users, (2) Scalable: can 2-3x volume without 2-3x effort, (3) Sustainable: not one-time spikes. Common winners: SEO/content (scalable, sustainable), community engagement (high quality), referrals (low cost). Cut or minimize: low-performing channels, one-off tactics, channels requiring constant manual effort. Document your top 3 with rationale for each.',
              text_zh: '🎯 关键：识别前 3 个渠道 - 根据您的审计，选择 3 个要关注的渠道。标准：(1) 已证明的性能：已经带来用户，(2) 可扩展：可以在不增加 2-3 倍努力的情况下增加 2-3 倍的数量，(3) 可持续：不是一次性的高峰。常见的赢家：SEO/内容（可扩展、可持续）、社区参与（高质量）、推荐（低成本）。削减或最小化：低性能渠道、一次性策略、需要持续手动努力的渠道。记录您的前 3 个渠道及其理由。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'week1'
            },
            {
              text: '🔄 CRITICAL: Map Conversion Funnel - Document your user journey from first touch to active user. Stages: (1) Visitor: lands on your site, (2) Signup: creates account, (3) Activation: completes first key action, (4) Retention: returns within 7 days. Measure conversion rate at each stage. Example: 1000 visitors → 100 signups (10%) → 50 activated (50%) → 30 retained (60%). Calculate overall conversion: visitors to retained users. Identify biggest drop-off point: this is your bottleneck to fix first.',
              text_zh: '🔄 关键：映射转化漏斗 - 记录您的用户旅程，从首次接触到活跃用户。阶段：(1) 访客：登陆您的网站，(2) 注册：创建账户，(3) 激活：完成第一个关键操作，(4) 留存：7 天内返回。测量每个阶段的转化率。示例：1000 名访客 → 100 次注册（10%）→ 50 次激活（50%）→ 30 次留存（60%）。计算总体转化率：访客到留存用户。识别最大的流失点：这是您首先要修复的瓶颈。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical',
              phase: 'week1'
            },
            {
              text: '⚡ CRITICAL: Optimize Biggest Bottleneck - Focus on improving your worst conversion rate. If signup→activation is low (<40%): improve onboarding (add tutorial, simplify first action, send activation email). If visitor→signup is low (<5%): improve landing page (clearer headline, better demo, stronger CTA). If activation→retention is low (<50%): improve core value delivery (faster time to value, better feature discovery). Run A/B tests on key elements. Tools: Google Optimize, Optimizely, or manual split testing. Target: ≥20% signup-to-activation rate. Measure improvement over 1 week.',
              text_zh: '⚡ 关键：优化最大瓶颈 - 专注于改善您最差的转化率。如果注册→激活率低（<40%）：改善入职（添加教程、简化第一个操作、发送激活电子邮件）。如果访客→注册率低（<5%）：改善登陆页面（更清晰的标题、更好的演示、更强的 CTA）。如果激活→留存率低（<50%）：改善核心价值交付（更快的价值实现时间、更好的功能发现）。对关键元素运行 A/B 测试。工具：Google Optimize、Optimizely 或手动拆分测试。目标：≥20% 的注册到激活率。测量 1 周内的改进。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical',
              phase: 'week1'
            },

            // WEEK 2: CONTENT & SEO
            {
              text: '📝 WEEK 2: CONTENT & SEO - Build organic growth engines',
              text_zh: '📝 第 2 周：内容与 SEO - 构建有机增长引擎',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'week2'
            },
            {
              text: '📅 IMPORTANT: Create Content Calendar - Plan 30 days of content across your top channels. Format: (1) 20 social media posts (Twitter/LinkedIn/Facebook): tips, use cases, customer wins, (2) 4 blog posts: how-to guides, case studies, industry insights, (3) 2-3 videos: product demos, tutorials, founder story. Schedule: 1 social post/day, 1 blog post/week, 1 video every 2 weeks. Tools: Buffer or Hootsuite for scheduling, Notion or Google Sheets for planning. AI assist: Use ChatGPT or Jasper to draft content faster. Focus: educational content that solves problems, not just product promotion.',
              text_zh: '📅 重要：创建内容日历 - 在您的顶级渠道上计划 30 天的内容。格式：(1) 20 个社交媒体帖子（Twitter/LinkedIn/Facebook）：技巧、用例、客户成功案例，(2) 4 篇博客文章：操作指南、案例研究、行业见解，(3) 2-3 个视频：产品演示、教程、创始人故事。时间表：每天 1 个社交帖子，每周 1 篇博客文章，每 2 周 1 个视频。工具：Buffer 或 Hootsuite 用于安排，Notion 或 Google Sheets 用于规划。AI 辅助：使用 ChatGPT 或 Jasper 更快地起草内容。重点：解决问题的教育内容，而不仅仅是产品推广。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important',
              phase: 'week2'
            },
            {
              text: '🔍 IMPORTANT: Implement SEO Strategy - Target long-tail keywords to drive organic traffic. Process: (1) Keyword research: Use Ahrefs, SEMrush, or Ubersuggest to find 10-15 keywords with: low competition (<30 difficulty), decent volume (100-1000 searches/month), high intent (people looking for solutions). (2) Create SEO-optimized content: Write blog posts targeting each keyword, include keyword in title, first paragraph, and headers, add internal links to your product. (3) Technical SEO: Ensure fast page load, mobile-friendly, proper meta descriptions. Target: 500+ organic visitors/month within 3 months. Track: Google Search Console for rankings and traffic.',
              text_zh: '🔍 重要：实施 SEO 策略 - 针对长尾关键词以推动自然流量。流程：(1) 关键词研究：使用 Ahrefs、SEMrush 或 Ubersuggest 查找 10-15 个关键词，具有：低竞争（<30 难度）、适当的搜索量（100-1000 次搜索/月）、高意图（寻找解决方案的人）。(2) 创建 SEO 优化的内容：针对每个关键词撰写博客文章，在标题、第一段和标题中包含关键词，添加指向您产品的内部链接。(3) 技术 SEO：确保快速页面加载、移动友好、适当的元描述。目标：3 个月内 500+ 自然访客/月。跟踪：Google Search Console 用于排名和流量。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important',
              phase: 'week2'
            },
            {
              text: '📧 IMPORTANT: Build Email Nurture Sequence - Create automated email sequence to convert signups into active users. Sequence: (1) Welcome email (sent immediately): introduce yourself, set expectations, link to quick start guide. (2) Day 2: Share success story or use case. (3) Day 4: Provide tips and best practices. (4) Day 7: Request feedback and offer help. (5) Day 14: Highlight advanced features or upgrade options. Tools: Mailchimp (free up to 500 contacts), ConvertKit (creator-friendly), or Customer.io (advanced automation). Target: ≥25% email open rate, ≥5% click-through rate. Track: opens, clicks, conversions to active users.',
              text_zh: '📧 重要：构建电子邮件培育序列 - 创建自动化电子邮件序列以将注册转化为活跃用户。序列：(1) 欢迎电子邮件（立即发送）：介绍自己，设定期望，链接到快速入门指南。(2) 第 2 天：分享成功故事或用例。(3) 第 4 天：提供技巧和最佳实践。(4) 第 7 天：请求反馈并提供帮助。(5) 第 14 天：突出高级功能或升级选项。工具：Mailchimp（最多 500 个联系人免费）、ConvertKit（对创作者友好）或 Customer.io（高级自动化）。目标：≥25% 的电子邮件打开率，≥5% 的点击率。跟踪：打开、点击、转化为活跃用户。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important',
              phase: 'week2'
            },

            // WEEK 3: PAID & PARTNERSHIPS
            {
              text: '💰 WEEK 3: PAID & PARTNERSHIPS - Test paid channels and build strategic relationships',
              text_zh: '💰 第 3 周：付费与合作 - 测试付费渠道并建立战略关系',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'week3'
            },
            {
              text: '💳 OPTIONAL: Test Paid Channels - Start small with paid advertising to accelerate growth. Budget: $500-1000 for testing. Channels to test: (1) Facebook/Instagram Ads: good for B2C, visual products, (2) Google Ads: good for high-intent searches, (3) Twitter/X Ads: good for tech/SaaS products. Process: Create 3-5 ad variations, target narrow audience (your ideal customer), run for 1 week, measure cost per signup. Target: <$15 cost per signup. If you hit target, scale budget 2x. If not, pause and focus on organic channels. Tools: Facebook Ads Manager, Google Ads, Twitter Ads Manager. Track: impressions, clicks, signups, cost per acquisition.',
              text_zh: '💳 可选：测试付费渠道 - 从小规模付费广告开始以加速增长。预算：$500-1000 用于测试。要测试的渠道：(1) Facebook/Instagram 广告：适合 B2C、视觉产品，(2) Google 广告：适合高意图搜索，(3) Twitter/X 广告：适合技术/SaaS 产品。流程：创建 3-5 个广告变体，针对狭窄的受众（您的理想客户），运行 1 周，测量每次注册的成本。目标：<$15 每次注册成本。如果达到目标，将预算扩大 2 倍。如果没有，暂停并专注于有机渠道。工具：Facebook Ads Manager、Google Ads、Twitter Ads Manager。跟踪：展示次数、点击次数、注册次数、获取成本。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'optional',
              phase: 'week3'
            },
            {
              text: '🤝 OPTIONAL: Build Strategic Partnerships - Partner with complementary tools to reach their audience. Process: (1) Identify 10 complementary tools (not competitors): tools your users already use or would benefit from. (2) Research contact: find founder/marketing lead on LinkedIn, Twitter, or company website. (3) Propose partnership: integration (connect your tools), co-marketing (joint webinar, blog post swap), affiliate program (they promote you, earn commission). (4) Start small: 1-2 partnerships first, prove value before scaling. Target: 2-3 partnerships established, 50-100 users from partners. Focus: quality over quantity, mutual benefit.',
              text_zh: '🤝 可选：建立战略合作伙伴关系 - 与互补工具合作以接触他们的受众。流程：(1) 识别 10 个互补工具（非竞争对手）：您的用户已经使用或会受益的工具。(2) 研究联系人：在 LinkedIn、Twitter 或公司网站上找到创始人/营销负责人。(3) 提出合作：集成（连接您的工具）、联合营销（联合网络研讨会、博客文章交换）、联盟计划（他们推广您，赚取佣金）。(4) 从小开始：首先 1-2 个合作伙伴关系，在扩展之前证明价值。目标：建立 2-3 个合作伙伴关系，从合作伙伴获得 50-100 个用户。重点：质量而非数量，互惠互利。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'optional',
              phase: 'week3'
            },
            {
              text: '🎁 IMPORTANT: Implement Referral Program - Turn happy users into advocates. Design: "Give $10, Get $10" or "Refer 3 friends, get 1 month free". Implementation options: (1) Use tools: ReferralCandy, Viral Loops, GrowSurf (easiest, $50-100/month), (2) Build custom: generate unique referral links, track signups, reward automatically. Promotion: (1) Add referral CTA in product (after user completes action), (2) Include in email sequence, (3) Announce on social media. Target: ≥0.3 viral coefficient (each user brings 0.3 new users on average). Track: referral signups, conversion rate, top referrers. Reward top referrers with extra perks.',
              text_zh: '🎁 重要：实施推荐计划 - 将满意的用户变成倡导者。设计："赠送 $10，获得 $10"或"推荐 3 个朋友，获得 1 个月免费"。实施选项：(1) 使用工具：ReferralCandy、Viral Loops、GrowSurf（最简单，$50-100/月），(2) 自定义构建：生成唯一的推荐链接，跟踪注册，自动奖励。推广：(1) 在产品中添加推荐 CTA（用户完成操作后），(2) 包含在电子邮件序列中，(3) 在社交媒体上宣布。目标：≥0.3 病毒系数（每个用户平均带来 0.3 个新用户）。跟踪：推荐注册、转化率、顶级推荐人。用额外的福利奖励顶级推荐人。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important',
              phase: 'week3'
            },

            // WEEK 4: LAUNCH & SCALE
            {
              text: '🚀 WEEK 4: LAUNCH & SCALE - Execute major launch and track growth metrics',
              text_zh: '🚀 第 4 周：发布与扩展 - 执行重大发布并跟踪增长指标',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'week4'
            },
            {
              text: '🏆 IMPORTANT: Product Hunt Launch - Launch on Product Hunt for visibility and credibility. Preparation (1 week before): (1) Create assets: logo, 3-5 screenshots, demo video (60-90 seconds), tagline (10 words max), description (200 words). (2) Rally supporters: message 20+ friends/users to upvote and comment on launch day. (3) Schedule: launch on Tuesday-Thursday at 12:01am PST for maximum visibility. Launch day: (1) Post your product, (2) Respond to every comment within 1 hour, (3) Share on social media, (4) Monitor ranking throughout day. Target: Top 10 product of the day, 100-300 signups. Follow-up: thank supporters, convert signups to active users.',
              text_zh: '🏆 重要：Product Hunt 发布 - 在 Product Hunt 上发布以获得可见性和可信度。准备（提前 1 周）：(1) 创建资料：徽标、3-5 个屏幕截图、演示视频（60-90 秒）、标语（最多 10 个字）、描述（200 字）。(2) 召集支持者：在发布日向 20+ 个朋友/用户发送消息以投票和评论。(3) 安排：在周二至周四太平洋标准时间凌晨 12:01 发布以获得最大可见性。发布日：(1) 发布您的产品，(2) 在 1 小时内回复每条评论，(3) 在社交媒体上分享，(4) 全天监控排名。目标：当天前 10 名产品，100-300 次注册。后续：感谢支持者，将注册转化为活跃用户。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 20,
              priority: 'important',
              phase: 'week4'
            },
            {
              text: '📊 CRITICAL: Track Growth Metrics - Set up weekly metrics dashboard to monitor progress. Key metrics: (1) Total users: active users (used in last 7 days), (2) Weekly signups: new users this week, (3) Growth rate: (this week - last week) / last week × 100%, (4) Top channels: where users are coming from, (5) Activation rate: % of signups who complete first action, (6) Cost per acquisition: total marketing spend / new users. Tools: Google Sheets dashboard, Mixpanel, or Amplitude. Review: every Monday morning, identify what\'s working and what\'s not. Target: ≥500 active users, ≥15% monthly growth rate. Adjust strategy based on data: double down on winning channels, cut losing tactics.',
              text_zh: '📊 关键：跟踪增长指标 - 设置每周指标仪表板以监控进度。关键指标：(1) 总用户数：活跃用户（过去 7 天内使用），(2) 每周注册数：本周新用户，(3) 增长率：（本周 - 上周）/ 上周 × 100%，(4) 顶级渠道：用户来自哪里，(5) 激活率：完成第一个操作的注册百分比，(6) 获取成本：总营销支出 / 新用户。工具：Google Sheets 仪表板、Mixpanel 或 Amplitude。审查：每周一早上，识别什么有效，什么无效。目标：≥500 活跃用户，≥15% 月增长率。根据数据调整策略：加倍投入成功的渠道，削减失败的策略。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical',
              phase: 'week4'
            },

            // Definition of Done
            {
              text: '✅ View Definition of Done - See exactly what you need to complete for this step',
              text_zh: '✅ 查看完成定义 - 准确了解您需要为此步骤完成什么',
              type: 'definitionOfDone',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'critical'
            }
          ],
          dodCriteria: [
            'Top 3 growth channels identified and optimized',
            'Conversion funnel mapped with ≥20% signup-to-activation rate',
            '30-day content calendar created and scheduled',
            'SEO strategy implemented targeting 10+ keywords',
            'Referral program launched',
            '≥500 active users achieved',
            'Monthly growth rate ≥15% established',
            'Cost per acquisition <$15 (if using paid channels)'
          ],
          dodCriteria_zh: [
            '识别并优化前 3 个增长渠道',
            '映射转化漏斗，注册到激活率 ≥20%',
            '创建并安排 30 天内容日历',
            '实施针对 10+ 关键词的 SEO 策略',
            '启动推荐计划',
            '达到 ≥500 活跃用户',
            '建立 ≥15% 的月增长率',
            '获取成本 <$15（如果使用付费渠道）'
          ],
          timeEstimateWeeks: 4,
          status: 'locked'
        },
        {
          stepIndex: 3,
          title: 'Build Sustainable Revenue',
          title_zh: '建立可持续收入',
          description: 'Optimize monetization and build systems for long-term profitability',
          description_zh: '优化货币化并建立长期盈利系统',
          actions: [
            // Context action (Subtask 3.1)
            {
              text: '💡 WHY REVENUE OPTIMIZATION MATTERS: Growth without monetization leads to burnout and failure. Improving retention by 5% increases profits by 25-95%. At 500+ users, focus shifts from growth to sustainability: optimize pricing, reduce churn, improve unit economics, and build systems that generate revenue while you sleep.',
              text_zh: '💡 为什么收入优化很重要：没有货币化的增长会导致倦怠和失败。将留存率提高 5% 可使利润增加 25-95%。在 500+ 用户时，重点从增长转向可持续性：优化定价、减少流失、改善单位经济效益，并建立在您睡觉时产生收入的系统。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context'
            },

            // Unit economics and pricing actions (Subtask 3.2)
            {
              text: '📊 CRITICAL: Analyze Unit Economics - Calculate your business fundamentals to understand profitability. Formulas: (1) LTV (Lifetime Value) = Average Revenue Per User × Average Customer Lifespan (months), (2) CAC (Customer Acquisition Cost) = Total Marketing & Sales Spend ÷ New Customers, (3) LTV:CAC Ratio = LTV ÷ CAC. Target: LTV:CAC ≥3:1 (healthy), ≥4:1 (excellent). If <3:1, you\'re losing money on growth. Create spreadsheet tracking: monthly revenue per user, average lifespan, acquisition costs by channel. Identify: which channels are profitable? Where are you losing money?',
              text_zh: '📊 关键：分析单位经济效益 - 计算您的业务基本面以了解盈利能力。公式：(1) LTV（生命周期价值）= 每用户平均收入 × 平均客户生命周期（月），(2) CAC（客户获取成本）= 总营销和销售支出 ÷ 新客户，(3) LTV:CAC 比率 = LTV ÷ CAC。目标：LTV:CAC ≥3:1（健康），≥4:1（优秀）。如果 <3:1，您在增长上亏钱。创建电子表格跟踪：每用户月收入、平均生命周期、按渠道的获取成本。识别：哪些渠道有利可图？您在哪里亏钱？',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '💰 CRITICAL: Optimize Pricing - Test pricing changes to increase revenue without losing customers. Strategies: (1) Price increase test: Raise prices 10-20% for new users only, monitor conversion rate and revenue impact. (2) Tiered pricing: Create 3 tiers (Basic, Pro, Enterprise) with clear value differentiation. (3) Value-based pricing: Price based on customer value, not costs (e.g., "Save 10 hours/week" = $50/month). (4) A/B test: Test different price points with split traffic. Tools: Stripe for pricing experiments, ProfitWell for pricing analysis. Target: ≥20% revenue increase without >10% drop in conversion. Document: current pricing, test results, new pricing strategy.',
              text_zh: '💰 关键：优化定价 - 测试定价变化以在不失去客户的情况下增加收入。策略：(1) 价格上涨测试：仅对新用户提高价格 10-20%，监控转化率和收入影响。(2) 分层定价：创建 3 个层级（基础版、专业版、企业版），具有明确的价值差异。(3) 基于价值的定价：根据客户价值而非成本定价（例如，"每周节省 10 小时" = $50/月）。(4) A/B 测试：使用分流测试不同的价格点。工具：Stripe 用于定价实验，ProfitWell 用于定价分析。目标：≥20% 的收入增长，转化率下降 <10%。记录：当前定价、测试结果、新定价策略。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },

            // Retention and upsell actions (Subtask 3.3)
            {
              text: '🔄 CRITICAL: Reduce Churn - Identify why users leave and implement retention strategies. Analysis: (1) Exit surveys: "Why are you canceling?" (too expensive, not using it, missing features, found alternative). (2) Usage data: Do churned users have low engagement? Which features correlate with retention? (3) Cohort analysis: Which user segments churn most? Calculate: Monthly churn rate = (Customers Lost This Month ÷ Customers at Start of Month) × 100%. Target: <5% monthly churn. Retention tactics: (1) Win-back campaigns: Email churned users with special offers. (2) Proactive support: Reach out to low-engagement users before they churn. (3) Improve onboarding: Better activation = better retention. (4) Usage alerts: Notify users when they\'re not using key features. Tools: Intercom, Customer.io for automated campaigns.',
              text_zh: '🔄 关键：减少流失 - 识别用户离开的原因并实施留存策略。分析：(1) 退出调查："您为什么取消？"（太贵、不使用、缺少功能、找到替代品）。(2) 使用数据：流失用户的参与度低吗？哪些功能与留存相关？(3) 队列分析：哪些用户群体流失最多？计算：月流失率 = （本月流失的客户 ÷ 月初的客户）× 100%。目标：<5% 月流失率。留存策略：(1) 挽回活动：向流失用户发送特别优惠电子邮件。(2) 主动支持：在低参与度用户流失之前联系他们。(3) 改善入职：更好的激活 = 更好的留存。(4) 使用警报：当用户未使用关键功能时通知他们。工具：Intercom、Customer.io 用于自动化活动。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '⬆️ IMPORTANT: Implement Upsells - Convert free/basic users to paid/premium plans. Upsell strategies: (1) Usage-based triggers: "You\'ve hit your limit, upgrade for more." (2) Feature gates: Show premium features with upgrade prompts. (3) Time-based: Offer upgrade after 7-14 days of active use. (4) Value demonstration: "Users on Pro plan save 5 hours/week." (5) Limited-time offers: "Upgrade now, get 20% off first 3 months." Implementation: Add upgrade CTAs in product (non-intrusive), send targeted upgrade emails, create comparison page showing plan differences. Target: ≥15% of free/basic users upgrade to paid/premium. Track: upgrade rate, revenue from upgrades, time to upgrade. Tools: Stripe for billing, Appcues or Pendo for in-app prompts.',
              text_zh: '⬆️ 重要：实施升级销售 - 将免费/基础用户转化为付费/高级计划。升级销售策略：(1) 基于使用的触发器："您已达到限制，升级以获得更多。"(2) 功能门控：显示带有升级提示的高级功能。(3) 基于时间：在活跃使用 7-14 天后提供升级。(4) 价值展示："专业版用户每周节省 5 小时。"(5) 限时优惠："立即升级，前 3 个月享受 20% 折扣。"实施：在产品中添加升级 CTA（非侵入性），发送有针对性的升级电子邮件，创建显示计划差异的比较页面。目标：≥15% 的免费/基础用户升级到付费/高级。跟踪：升级率、升级收入、升级时间。工具：Stripe 用于计费，Appcues 或 Pendo 用于应用内提示。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important'
            },
            {
              text: '🎯 IMPORTANT: Build Retention Systems - Create automated systems to keep users engaged long-term. Systems to implement: (1) Email re-engagement: Automated campaigns for inactive users (e.g., "We miss you! Here\'s what\'s new"). (2) In-app engagement: Usage tips, feature discovery, success stories shown in product. (3) Community building: User forum, Slack group, or Facebook group for peer support and engagement. (4) Regular content: Weekly newsletter with tips, case studies, product updates. (5) Milestone celebrations: Congratulate users on achievements ("You\'ve completed 100 tasks!"). Target: ≥60% 90-day retention (users still active after 3 months). Measure: cohort retention curves, engagement metrics, community activity. Tools: Mailchimp/ConvertKit for email, Circle or Discord for community.',
              text_zh: '🎯 重要：建立留存系统 - 创建自动化系统以保持用户长期参与。要实施的系统：(1) 电子邮件重新参与：针对不活跃用户的自动化活动（例如，"我们想念您！这是新内容"）。(2) 应用内参与：在产品中显示使用技巧、功能发现、成功故事。(3) 社区建设：用户论坛、Slack 群组或 Facebook 群组以获得同行支持和参与。(4) 定期内容：每周通讯，包含技巧、案例研究、产品更新。(5) 里程碑庆祝：祝贺用户取得成就（"您已完成 100 个任务！"）。目标：≥60% 的 90 天留存率（3 个月后仍活跃的用户）。测量：队列留存曲线、参与度指标、社区活动。工具：Mailchimp/ConvertKit 用于电子邮件，Circle 或 Discord 用于社区。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important'
            },

            // Automation and analytics actions (Subtask 3.4)
            {
              text: '⚙️ IMPORTANT: Automate Operations - Identify and automate repetitive manual tasks to save time and scale efficiently. Process: (1) Audit time: Track what you do manually for 1 week (customer onboarding, billing, support, reporting). (2) Identify automation opportunities: Tasks done >3x per week, tasks with clear rules/logic, tasks that don\'t require human judgment. (3) Implement automation: Customer onboarding (automated email sequences, self-service tutorials), billing (automatic invoicing, payment reminders, subscription management), support (FAQ chatbot, help center, automated responses), reporting (scheduled reports, dashboard alerts). Tools: Zapier or Make for workflow automation, Intercom for support automation, Stripe for billing automation. Target: Save ≥10 hours per week. Document: tasks automated, time saved, automation workflows.',
              text_zh: '⚙️ 重要：自动化运营 - 识别并自动化重复的手动任务以节省时间并高效扩展。流程：(1) 审计时间：跟踪您手动执行的操作 1 周（客户入职、计费、支持、报告）。(2) 识别自动化机会：每周执行 >3 次的任务、具有明确规则/逻辑的任务、不需要人工判断的任务。(3) 实施自动化：客户入职（自动化电子邮件序列、自助教程），计费（自动开票、付款提醒、订阅管理），支持（FAQ 聊天机器人、帮助中心、自动回复），报告（计划报告、仪表板警报）。工具：Zapier 或 Make 用于工作流自动化，Intercom 用于支持自动化，Stripe 用于计费自动化。目标：每周节省 ≥10 小时。记录：自动化的任务、节省的时间、自动化工作流。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important'
            },
            {
              text: '📈 IMPORTANT: Implement Analytics Dashboard - Create real-time dashboard to track key business metrics and make data-driven decisions. Metrics to track: (1) Revenue metrics: MRR (Monthly Recurring Revenue), ARR (Annual Recurring Revenue), revenue growth rate, revenue per user. (2) Customer metrics: Total customers, new customers this month, churned customers, churn rate, customer lifetime. (3) Engagement metrics: Daily/Monthly Active Users (DAU/MAU), feature usage, session duration. (4) Financial metrics: LTV, CAC, LTV:CAC ratio, gross margin, burn rate. Dashboard tools: ChartMogul or Baremetrics (SaaS-specific, $50-100/month), Mixpanel or Amplitude (product analytics), Google Data Studio (free, custom dashboards), or build custom with Retool. Setup: Connect data sources (Stripe, database, analytics), create visualizations, set up alerts for key thresholds. Review: Weekly metrics review every Monday. Target: All key metrics visible in one place, updated in real-time.',
              text_zh: '📈 重要：实施分析仪表板 - 创建实时仪表板以跟踪关键业务指标并做出数据驱动的决策。要跟踪的指标：(1) 收入指标：MRR（月度经常性收入）、ARR（年度经常性收入）、收入增长率、每用户收入。(2) 客户指标：总客户数、本月新客户、流失客户、流失率、客户生命周期。(3) 参与度指标：每日/每月活跃用户（DAU/MAU）、功能使用、会话持续时间。(4) 财务指标：LTV、CAC、LTV:CAC 比率、毛利率、燃烧率。仪表板工具：ChartMogul 或 Baremetrics（SaaS 专用，$50-100/月），Mixpanel 或 Amplitude（产品分析），Google Data Studio（免费，自定义仪表板），或使用 Retool 构建自定义。设置：连接数据源（Stripe、数据库、分析），创建可视化，为关键阈值设置警报。审查：每周一进行每周指标审查。目标：所有关键指标在一个地方可见，实时更新。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important'
            },
            {
              text: '💼 IMPORTANT: Create Financial Projections - Build 6-month financial model to plan for profitability and growth. Model components: (1) Revenue forecast: Current MRR, expected growth rate (conservative, realistic, optimistic), new customer acquisition, expansion revenue from upsells, churn impact. (2) Expense forecast: Fixed costs (hosting, tools, salaries), variable costs (marketing spend, support costs), one-time costs (development, equipment). (3) Cash flow: Monthly cash in vs cash out, runway calculation (months until $0), break-even point. (4) Scenarios: Best case (high growth, low churn), base case (realistic), worst case (low growth, high churn). Tools: Google Sheets or Excel with formulas, financial modeling templates (search "SaaS financial model template"). Output: Monthly P&L projection, cash flow forecast, runway calculation, break-even analysis. Use for: Fundraising decisions, hiring plans, pricing strategy, growth targets. Update: Monthly with actual results.',
              text_zh: '💼 重要：创建财务预测 - 构建 6 个月财务模型以规划盈利能力和增长。模型组成部分：(1) 收入预测：当前 MRR、预期增长率（保守、现实、乐观）、新客户获取、升级销售的扩展收入、流失影响。(2) 费用预测：固定成本（托管、工具、工资）、可变成本（营销支出、支持成本）、一次性成本（开发、设备）。(3) 现金流：每月现金流入与流出、跑道计算（到 $0 的月数）、盈亏平衡点。(4) 场景：最佳情况（高增长、低流失）、基本情况（现实）、最坏情况（低增长、高流失）。工具：带公式的 Google Sheets 或 Excel，财务建模模板（搜索"SaaS 财务模型模板"）。输出：月度损益预测、现金流预测、跑道计算、盈亏平衡分析。用于：筹款决策、招聘计划、定价策略、增长目标。更新：每月使用实际结果。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important'
            },

            // Definition of Done (Subtask 3.5)
            {
              text: '✅ View Definition of Done - See exactly what you need to complete for this step',
              text_zh: '✅ 查看完成定义 - 准确了解您需要为此步骤完成什么',
              type: 'definitionOfDone',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'critical'
            }
          ],
          dodCriteria: [
            'Unit economics calculated with LTV:CAC ratio ≥3:1',
            'Pricing optimized with ≥20% revenue increase achieved',
            'Churn rate reduced to <5% monthly',
            'Upsell system implemented with ≥15% upgrade rate',
            'Retention systems in place with ≥60% 90-day retention',
            '≥10 hours per week automated through workflow optimization',
            'Analytics dashboard tracking all key metrics (MRR, churn, LTV, CAC)',
            '6-month financial projections created with scenarios',
            'Monthly recurring revenue ≥$5,000 achieved'
          ],
          dodCriteria_zh: [
            '计算单位经济效益，LTV:CAC 比率 ≥3:1',
            '优化定价，实现 ≥20% 的收入增长',
            '将流失率降低到每月 <5%',
            '实施升级销售系统，升级率 ≥15%',
            '建立留存系统，90 天留存率 ≥60%',
            '通过工作流优化每周自动化 ≥10 小时',
            '分析仪表板跟踪所有关键指标（MRR、流失、LTV、CAC）',
            '创建包含场景的 6 个月财务预测',
            '实现月度经常性收入 ≥$5,000'
          ],
          timeEstimateWeeks: 4,
          status: 'locked'
        }
      ]
    };

    // Get the steps for the stage
    const steps = baseStepsRaw[stage] || baseStepsRaw['Beginner'];

    // Add translations to all actions
    return steps.map(step => ({
      ...step,
      actions: addTranslationsToActions(step.actions)
    }));
  }

  /**
   * Applies persona-specific modifications to base steps
   */
  private static applyPersonaModifications(
    steps: GeneratedStep[],
    persona: JourneyPersona,
    market: JourneyMarket,
    stage: JourneyStage
  ): GeneratedStep[] {
    switch (persona) {
      case 'skilled-maker':
        return this.applySkilledMakerModifications(steps, stage);

      case 'expert-service':
        return this.applyExpertServiceModifications(steps, market, stage);

      case 'founder-plan':
        return this.applyFounderPlanModifications(steps, stage);

      default:
        return steps;
    }
  }

  /**
   * Skilled-maker persona modifications
   * Focus: AI-powered idea generation and systematic validation
   * For In Progress: Emphasize technical metrics, technical communities, API monetization
   */
  private static applySkilledMakerModifications(
    steps: GeneratedStep[],
    stage: JourneyStage
  ): GeneratedStep[] {
    if (stage === 'Beginner' && steps[0]) {
      steps[0] = {
        ...steps[0],
        title: 'Generate and Validate Ideas',
        title_zh: '生成和验证创意',
        description: 'Use AI-powered idea generation and systematic validation',
        description_zh: '使用 AI 驱动的创意生成和系统化验证',
        actions: [
          {
            text: 'Generate Ideas with AI',
            text_zh: '使用 AI 生成创意',
            type: 'ai',
            pointsCost: -5,
            pointsReward: 15,
          }
        ],
        dodCriteria: [
          '≥3 AI-generated ideas evaluated',
          'Top idea validated with 40% problem frequency',
          'Technical feasibility assessed',
          'Market opportunity quantified'
        ]
      };
    }

    // In Progress stage modifications for skilled-maker
    if (stage === 'In Progress') {
      // Step 0: Emphasize technical metrics
      if (steps[0]) {
        const step0Actions = steps[0].actions.map(action => {
          if (action.text.includes('Analyze Key Metrics')) {
            return {
              ...action,
              text: action.text + ' For technical products, also track: API response times (<200ms target), error rates (<0.1%), uptime (≥99.9%), feature adoption by technical users.',
              text_zh: action.text_zh + ' 对于技术产品，还要跟踪：API 响应时间（<200ms 目标）、错误率（<0.1%）、正常运行时间（≥99.9%）、技术用户的功能采用率。'
            };
          }
          return action;
        });
        steps[0] = { ...steps[0], actions: step0Actions };
      }

      // Step 2: Emphasize technical communities
      if (steps[2]) {
        const step2Actions = steps[2].actions.map(action => {
          if (action.text.includes('Post in relevant communities')) {
            return {
              ...action,
              text: '🌐 IMPORTANT: Post in technical communities - Share in: GitHub (create open-source tools/libraries), Stack Overflow (answer questions, mention your tool), Dev.to (write technical tutorials), Hacker News (Show HN posts), Reddit (r/programming, r/webdev, r/devtools), Product Hunt (developer tools category), Discord/Slack dev communities. Focus on providing technical value first, product mention second. Target: 5-10 technical communities.',
              text_zh: '🌐 重要：在技术社区发布 - 分享到：GitHub（创建开源工具/库）、Stack Overflow（回答问题，提及您的工具）、Dev.to（撰写技术教程）、Hacker News（Show HN 帖子）、Reddit（r/programming、r/webdev、r/devtools）、Product Hunt（开发者工具类别）、Discord/Slack 开发社区。首先专注于提供技术价值，其次是产品提及。目标：5-10 个技术社区。'
            };
          }
          return action;
        });
        steps[2] = { ...steps[2], actions: step2Actions };
      }

      // Step 3: Emphasize API monetization
      if (steps[3]) {
        const step3Actions = steps[3].actions.map(action => {
          if (action.text.includes('Optimize Pricing')) {
            return {
              ...action,
              text: action.text + ' For API/developer tools, consider: usage-based pricing (per API call, per GB processed), tiered pricing by request volume, freemium with generous free tier for developers to test. Example: Free (1K requests/month) → Pro ($49, 100K requests) → Enterprise (custom, unlimited).',
              text_zh: action.text_zh + ' 对于 API/开发者工具，考虑：基于使用的定价（每次 API 调用、每 GB 处理）、按请求量分层定价、为开发者提供慷慨免费层的免费增值模式以供测试。示例：免费（1K 请求/月）→ 专业版（$49，100K 请求）→ 企业版（自定义，无限制）。'
            };
          }
          return action;
        });
        steps[3] = { ...steps[3], actions: step3Actions };
      }
    }

    return steps;
  }

  /**
   * Expert-service persona modifications
   * Focus: Leverage existing audience/expertise for product development
   */
  private static applyExpertServiceModifications(
    steps: GeneratedStep[],
    market: JourneyMarket,
    stage: JourneyStage
  ): GeneratedStep[] {
    // Special handling for Social Media/Influencer market in Beginner stage
    if (stage === 'Beginner' && market === 'Social Media/Influencer' && steps[0]) {
      steps[0] = {
        ...steps[0],
        title: 'Identify Monetization Niche',
        title_zh: '识别变现领域',
        description: 'Based on existing follower profile, find the most suitable monetization direction',
        description_zh: '基于现有粉丝画像，找到最适合的变现方向',
        actions: [
          {
            text: '💡 WHY MONETIZATION MATTERS: You\'ve built an audience—now it\'s time to turn attention into revenue. Influencers who monetize strategically earn 10-50x more than those relying only on brand deals. Your audience trusts you and wants solutions to their problems. A well-designed product serves them while creating scalable income for you.',
            text_zh: '💡 为什么变现很重要：您已经建立了受众——现在是时候将注意力转化为收入了。战略性变现的网红比仅依赖品牌合作的网红收入高 10-50 倍。您的受众信任您并希望解决他们的问题。精心设计的产品既能服务他们，又能为您创造可扩展的收入。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'context'
          },
          {
            text: '📏 IMPORTANT: Understanding Metric Scaling - All targets in this journey are baseline numbers that should be scaled based on YOUR audience size. General rule: Larger audiences can reach higher absolute numbers but typically see lower conversion rates. Scaling guidelines: <5K followers = use 50-70% of baseline targets, 5K-10K = use 70-100% of baseline, 10K-30K = use 100-150% of baseline, 30K-50K = use 150-200%, 50K-100K = use 200-300%, 100K+ = use 300-500%. Example: If baseline says "50 survey responses," a 50K follower account should aim for 100-150 responses (200-300% of baseline). Throughout this journey, you\'ll see specific scaling guidance for each metric. Don\'t compare yourself to influencers with different audience sizes—focus on YOUR conversion rates and engagement quality.',
            text_zh: '📏 重要：理解指标调整 - 此旅程中的所有目标都是基准数字，应根据您的受众规模进行调整。一般规则：较大的受众可以达到更高的绝对数字，但通常转化率较低。调整指南：<5K 粉丝 = 使用基准目标的 50-70%，5K-10K = 使用 70-100% 的基准，10K-30K = 使用 100-150% 的基准，30K-50K = 使用 150-200%，50K-100K = 使用 200-300%，100K+ = 使用 300-500%。示例：如果基准说"50 个调查回复"，一个 50K 粉丝账户应该争取 100-150 个回复（基准的 200-300%）。在整个旅程中，您将看到每个指标的具体调整指导。不要将自己与不同受众规模的网红进行比较——专注于您的转化率和参与质量。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'important'
          },
          {
            text: '📊 CRITICAL: Analyze follower demand - Review your content performance data: (1) Which posts get highest engagement? (2) What questions do followers ask repeatedly in comments/DMs? (3) What problems do they mention most? (4) Which topics drive the most saves/shares? Use platform analytics (Instagram Insights, YouTube Analytics, 小红书数据中心, 抖音创作者服务中心) to identify patterns. Document top 5 pain points your audience experiences.',
            text_zh: '📊 关键：分析粉丝需求 - 查看您的内容表现数据：(1) 哪些帖子获得最高互动？(2) 粉丝在评论/私信中反复问什么问题？(3) 他们最常提到什么问题？(4) 哪些主题带来最多的收藏/分享？使用平台分析（Instagram Insights、YouTube Analytics、小红书数据中心、抖音创作者服务中心）识别模式。记录受众遇到的前 5 个痛点。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'critical'
          },
          {
            text: '📋 CRITICAL: Survey audience about product preferences - Create a survey asking: (1) "What\'s your biggest challenge with [your niche]?" (2) "What format would help you most?" (course/template/community/digital product), (3) "What would you pay for a solution?" (price ranges), (4) "What have you tried before?" (competitor research). Share survey via: Instagram Stories poll, YouTube Community post, 小红书笔记, 抖音评论区置顶, email list, 微信朋友圈. Target: ≥50 responses.',
            text_zh: '📋 关键：调查受众的产品偏好 - 创建调查询问：(1) "您在 [您的领域] 中最大的挑战是什么？"(2) "什么格式对您最有帮助？"（课程/模板/社区/数字产品），(3) "您愿意为解决方案支付多少？"（价格范围），(4) "您之前尝试过什么？"（竞争对手研究）。通过以下方式分享调查：Instagram Stories 投票、YouTube Community 帖子、小红书笔记、抖音评论区置顶、电子邮件列表、微信朋友圈。目标：≥50 个回复。',
            type: 'survey',
            pointsCost: -5,
            pointsReward: 15,
            priority: 'critical'
          },
          {
            text: '🔍 CRITICAL: Research competitor monetization models - Identify 5-10 influencers in your niche who successfully monetize. For each, document: (1) What products do they sell? (course/template/community/digital product), (2) What\'s their pricing? (3) How do they promote it? (4) What do customers say in reviews? (5) What\'s missing or could be better? Find your differentiation angle: What can you offer that\'s unique or better?',
            text_zh: '🔍 关键：研究竞争对手变现模式 - 识别您领域中 5-10 个成功变现的网红。对于每个人，记录：(1) 他们销售什么产品？（课程/模板/社区/数字产品），(2) 他们的定价是多少？(3) 他们如何推广？(4) 客户在评论中说什么？(5) 缺少什么或可以更好？找到您的差异化角度：您可以提供什么独特或更好的东西？',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'critical'
          },
          {
            text: '🎯 CRITICAL: Choose product format - Based on survey results and your strengths, select ONE format: (1) Online Course: Best if you teach processes/skills (fitness routines, design tutorials, business strategies). Platforms: Teachable, Kajabi, Gumroad (Western) or 小鹅通, 千聊, 知识星球 (Chinese). (2) Digital Template: Best if you have repeatable systems (Notion templates, spreadsheets, Canva designs, checklists). Platforms: Gumroad, Etsy (Western) or 抖音小店, 淘宝, 闲鱼 (Chinese). (3) Membership Community: Best if you provide ongoing support/accountability (fitness groups, business masterminds). Platforms: Discord, Circle, Patreon (Western) or 微信群, 知识星球, QQ群 (Chinese). (4) Digital Product: Best for one-time deliverables (ebooks, guides, presets, worksheets). Platforms: Gumroad, Stan Store (Western) or 抖音小店, 微信小商店 (Chinese). Document your choice and rationale.',
            text_zh: '🎯 关键：选择产品格式 - 根据调查结果和您的优势，选择一种格式：(1) 在线课程：如果您教授流程/技能（健身例程、设计教程、商业策略），最佳选择。平台：Teachable、Kajabi、Gumroad（西方）或小鹅通、千聊、知识星球（中国）。(2) 数字模板：如果您有可重复的系统（Notion 模板、电子表格、Canva 设计、清单），最佳选择。平台：Gumroad、Etsy（西方）或抖音小店、淘宝、闲鱼（中国）。(3) 会员社区：如果您提供持续支持/问责（健身小组、商业大师班），最佳选择。平台：Discord、Circle、Patreon（西方）或微信群、知识星球、QQ群（中国）。(4) 数字产品：适合一次性交付物（电子书、指南、预设、工作表）。平台：Gumroad、Stan Store（西方）或抖音小店、微信小商店（中国）。记录您的选择和理由。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'critical'
          },
          {
            text: '💰 IMPORTANT: Research product pricing for your niche - Find 10-15 similar products from influencers in your space. Create a spreadsheet tracking: product type, price, what\'s included, follower count of creator. Calculate pricing ranges: low ($X-$Y), mid ($Y-$Z), high ($Z+). Consider your audience: Western audiences typically pay $50-$500 for courses, $10-$100 for templates. Chinese audiences: ¥99-¥999 for courses, ¥9.9-¥99 for templates. Factor in your follower count: <10K followers = lower end, 10K-100K = mid-range, 100K+ = premium pricing. Select your target price range and validate with survey data.',
            text_zh: '💰 重要：研究您领域的产品定价 - 从您领域的网红那里找到 10-15 个类似产品。创建电子表格跟踪：产品类型、价格、包含内容、创作者的粉丝数。计算价格范围：低（$X-$Y）、中（$Y-$Z）、高（$Z+）。考虑您的受众：西方受众通常为课程支付 $50-$500，为模板支付 $10-$100。中国受众：课程 ¥99-¥999，模板 ¥9.9-¥99。考虑您的粉丝数：<10K 粉丝 = 较低端，10K-100K = 中档，100K+ = 高端定价。选择您的目标价格范围并通过调查数据验证。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important'
          },
          {
            text: '🔍 IMPORTANT: Analyze 5-10 competitor products in detail - For each competitor product, document: (1) Strengths: What do they do well? What do customers praise? (2) Weaknesses: What complaints appear in reviews? What\'s missing? (3) Pricing strategy: How do they structure tiers/bundles? (4) Marketing approach: How do they promote? What messaging works? (5) Target customer: Who buys this? Create a competitive analysis document. Identify gaps you can fill and advantages you have (unique expertise, better community, more personalized approach, etc.).',
            text_zh: '🔍 重要：详细分析 5-10 个竞争对手产品 - 对于每个竞争对手产品，记录：(1) 优势：他们做得好什么？客户称赞什么？(2) 劣势：评论中出现什么投诉？缺少什么？(3) 定价策略：他们如何构建层级/捆绑？(4) 营销方法：他们如何推广？什么信息有效？(5) 目标客户：谁购买这个？创建竞争分析文档。识别您可以填补的空白和您拥有的优势（独特的专业知识、更好的社区、更个性化的方法等）。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important'
          },
          {
            text: '🔄 IMPORTANT: Plan content-product integration strategy - Define how your product fits into your content ecosystem: (1) Content funnel: Awareness content (free tips) → Interest content (case studies) → Desire content (testimonials) → Action (product launch). (2) Promotion rhythm: How often will you mention the product? (Weekly? Monthly launches?). (3) Value delivery: How does the product complement your free content? (Free content = tips, Paid product = complete system). (4) Platform strategy: Western: Instagram Stories → email list → sales page. Chinese: 小红书/抖音 → 微信私域 → 直播/朋友圈转化. Document your 90-day content-to-product integration plan.',
            text_zh: '🔄 重要：规划内容-产品整合策略 - 定义您的产品如何融入您的内容生态系统：(1) 内容漏斗：认知内容（免费技巧）→ 兴趣内容（案例研究）→ 欲望内容（推荐）→ 行动（产品发布）。(2) 推广节奏：您多久提及一次产品？（每周？每月发布？）。(3) 价值交付：产品如何补充您的免费内容？（免费内容 = 技巧，付费产品 = 完整系统）。(4) 平台策略：西方：Instagram Stories → 电子邮件列表 → 销售页面。中国：小红书/抖音 → 微信私域 → 直播/朋友圈转化。记录您的 90 天内容到产品整合计划。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important'
          }
        ],
        dodCriteria: [
          '≥50 audience members surveyed about product preferences (scale: <5K followers=30+, 5K-20K=50+, 20K-50K=100+, 50K+=200+)',
          'Product format selected (course/template/community/digital product)',
          'Pricing strategy validated with target price range',
          '5-10 competitor products analyzed with differentiation documented',
          'Content-product integration strategy created',
          'Top 5 audience pain points identified from data analysis',
          'Platform selection completed based on cultural context'
        ],
        dodCriteria_zh: [
          '≥50 名受众成员接受产品偏好调查（按规模调整：<5K 粉丝=30+，5K-20K=50+，20K-50K=100+，50K+=200+）',
          '选择产品格式（课程/模板/社区/数字产品）',
          '验证定价策略并确定目标价格范围',
          '分析 5-10 个竞争对手产品并记录差异化',
          '创建内容-产品整合策略',
          '从数据分析中识别前 5 个受众痛点',
          '根据文化背景完成平台选择'
        ],
        timeEstimateWeeks: 1
      };

      // Step 1: Create Minimum Viable Product
      if (steps[1]) {
        steps[1] = {
          ...steps[1],
          title: 'Create Minimum Viable Product',
          title_zh: '创建最小可行产品',
          description: 'Build your first sellable content product as quickly as possible',
          description_zh: '尽快构建您的第一个可销售内容产品',
          actions: [
            {
              text: '💡 WHY MVP MATTERS: Your audience is waiting for a solution. A simple, focused product launched in 2-3 weeks beats a perfect product that takes 6 months. Start with ONE product format, get it to market, learn from real customers, then improve. Speed to market = speed to revenue.',
              text_zh: '💡 为什么 MVP 很重要：您的受众正在等待解决方案。在 2-3 周内推出的简单、专注的产品胜过需要 6 个月的完美产品。从一种产品格式开始，将其推向市场，从真实客户那里学习，然后改进。上市速度 = 收入速度。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context'
            },
            // Online Course Creation Path
            {
              text: '📚 OPTION A: CREATE ONLINE COURSE - Best if you teach processes, skills, or transformations',
              text_zh: '📚 选项 A：创建在线课程 - 如果您教授流程、技能或转型，最佳选择',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context'
            },
            {
              text: '📝 CRITICAL (Course): Outline course structure - Create 3-5 modules, each with 3-5 lessons. Keep lessons short: 5-15 minutes each. Total course length: 2-4 hours maximum for MVP. Example structure: Module 1 (Foundation), Module 2-4 (Core Content), Module 5 (Implementation). Document: module titles, lesson titles, key takeaways for each lesson. Focus on transformation, not information dump.',
              text_zh: '📝 关键（课程）：概述课程结构 - 创建 3-5 个模块，每个模块 3-5 节课。保持课程简短：每节 5-15 分钟。MVP 的总课程长度：最多 2-4 小时。示例结构：模块 1（基础），模块 2-4（核心内容），模块 5（实施）。记录：模块标题、课程标题、每节课的关键要点。专注于转型，而不是信息倾倒。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical'
            },
            {
              text: '🎥 CRITICAL (Course): Record core lessons - Phone camera is fine for MVP! Focus on value, not production quality. Setup: Good lighting (face a window), clean background, clear audio (use phone mic or $20 lavalier). Record first 5-7 lessons (30-40% of course). Script key points but speak naturally. Tools: Phone camera + iMovie/CapCut for basic editing. Pro tip: Record in batches (all Module 1 in one session) for consistency.',
              text_zh: '🎥 关键（课程）：录制核心课程 - 手机摄像头对 MVP 来说就足够了！专注于价值，而不是制作质量。设置：良好的照明（面向窗户）、干净的背景、清晰的音频（使用手机麦克风或 $20 领夹式麦克风）。录制前 5-7 节课（课程的 30-40%）。编写关键点脚本但自然说话。工具：手机摄像头 + iMovie/CapCut 进行基本编辑。专业提示：批量录制（一次录制所有模块 1）以保持一致性。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '🏫 IMPORTANT (Course): Choose course platform - Western: Teachable (easiest, $39/mo), Kajabi (all-in-one, $149/mo), Thinkific (free plan available), Podia (simple, $39/mo), Gumroad (pay-per-sale, 10% fee). Chinese: 小鹅通 (comprehensive, ¥4800/year), 千聊 (live-focused, ¥3000/year), 知识星球 (community+course, ¥3000/year), 荔枝微课 (simple, ¥2000/year). Choose based on: budget, features needed, audience location. Start with free/cheap option, upgrade later.',
              text_zh: '🏫 重要（课程）：选择课程平台 - 西方：Teachable（最简单，$39/月），Kajabi（一体化，$149/月），Thinkific（有免费计划），Podia（简单，$39/月），Gumroad（按销售付费，10% 费用）。中国：小鹅通（综合，¥4800/年），千聊（直播重点，¥3000/年），知识星球（社区+课程，¥3000/年），荔枝微课（简单，¥2000/年）。根据以下选择：预算、所需功能、受众位置。从免费/便宜的选项开始，稍后升级。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            {
              text: '⚙️ IMPORTANT (Course): Set up course and upload content - Create account on chosen platform. Upload videos (platform handles hosting). Add lesson descriptions and resources (PDFs, templates, worksheets). Create simple quizzes (3-5 questions per module) to boost engagement. Set up course structure: sequential (unlock lessons in order) or open (access all at once). Test: go through course as a student to catch issues.',
              text_zh: '⚙️ 重要（课程）：设置课程并上传内容 - 在选定的平台上创建账户。上传视频（平台处理托管）。添加课程描述和资源（PDF、模板、工作表）。创建简单的测验（每个模块 3-5 个问题）以提高参与度。设置课程结构：顺序（按顺序解锁课程）或开放（一次访问所有）。测试：作为学生浏览课程以发现问题。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            {
              text: '📄 CRITICAL (Course): Create sales page - Include: (1) Transformation promise: "Go from X to Y in Z weeks", (2) Course curriculum: module and lesson breakdown, (3) Testimonials: use beta tester feedback, (4) Preview lesson: free sample video, (5) Pricing and CTA: clear "Enroll Now" button. Use platform\'s built-in sales page or create custom with Carrd/Webflow. Keep it simple: 1-page scroll with clear sections.',
              text_zh: '📄 关键（课程）：创建销售页面 - 包括：(1) 转型承诺："在 Z 周内从 X 到 Y"，(2) 课程大纲：模块和课程细分，(3) 推荐：使用测试用户反馈，(4) 预览课程：免费样本视频，(5) 定价和 CTA：清晰的"立即注册"按钮。使用平台的内置销售页面或使用 Carrd/Webflow 创建自定义页面。保持简单：1 页滚动，部分清晰。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            // Digital Template Creation Path
            {
              text: '📋 OPTION B: CREATE DIGITAL TEMPLATE - Best if you have repeatable systems, frameworks, or tools',
              text_zh: '📋 选项 B：创建数字模板 - 如果您有可重复的系统、框架或工具，最佳选择',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context'
            },
            {
              text: '🔍 CRITICAL (Template): Identify your best template - Review your content: What tools/systems do you use repeatedly? What do followers ask for most? Options: Notion templates (dashboards, trackers, databases), Spreadsheets (calculators, planners, budgets), Canva templates (social media, presentations, graphics), Design files (Figma, Adobe), Checklists/worksheets (PDFs). Choose ONE that solves a specific problem. Example: "Content Calendar Template" not "Productivity System".',
              text_zh: '🔍 关键（模板）：识别您最好的模板 - 查看您的内容：您重复使用什么工具/系统？粉丝最常要求什么？选项：Notion 模板（仪表板、跟踪器、数据库），电子表格（计算器、规划器、预算），Canva 模板（社交媒体、演示文稿、图形），设计文件（Figma、Adobe），清单/工作表（PDF）。选择一个解决特定问题的。示例："内容日历模板"而不是"生产力系统"。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical'
            },
            {
              text: '🧹 CRITICAL (Template): Clean and document template - Remove personal information and example data. Add clear instructions: (1) "How to Use" section at top, (2) Tooltips/comments explaining each section, (3) Example data showing how to fill it out. Test with beginners: give to 2-3 people unfamiliar with it, watch them use it, fix confusing parts. Make it plug-and-play: users should understand it in 5 minutes without asking questions.',
              text_zh: '🧹 关键（模板）：清理和记录模板 - 删除个人信息和示例数据。添加清晰的说明：(1) 顶部的"如何使用"部分，(2) 解释每个部分的工具提示/注释，(3) 显示如何填写的示例数据。与初学者测试：给 2-3 个不熟悉它的人，观察他们使用它，修复令人困惑的部分。使其即插即用：用户应该在 5 分钟内理解它，无需提问。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '🏪 IMPORTANT (Template): Choose distribution platform - Western: Gumroad (easiest, 10% fee), Notion Template Gallery (free, built-in audience), Etsy (for design templates, $0.20/listing), Creative Market (for design files, 30% commission), Own website (Carrd + Stripe, full control). Chinese: 抖音小店 (integrated with Douyin, 2-5% fee), 淘宝 (established marketplace, fees vary), 闲鱼 (peer-to-peer, lower fees), 微信小商店 (WeChat ecosystem, 0.6% fee). Choose based on: where your audience shops, fees, ease of setup.',
              text_zh: '🏪 重要（模板）：选择分发平台 - 西方：Gumroad（最简单，10% 费用），Notion Template Gallery（免费，内置受众），Etsy（用于设计模板，$0.20/列表），Creative Market（用于设计文件，30% 佣金），自己的网站（Carrd + Stripe，完全控制）。中国：抖音小店（与抖音集成，2-5% 费用），淘宝（成熟的市场，费用不同），闲鱼（点对点，较低费用），微信小商店（微信生态系统，0.6% 费用）。根据以下选择：您的受众在哪里购物、费用、设置的便利性。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            {
              text: '📸 IMPORTANT (Template): Create preview content - Screenshots: 5-7 high-quality images showing different sections. Video walkthrough: 2-3 minute Loom video showing how to use it. Before/after examples: show empty template vs filled-out version. Feature highlights: list key features and benefits. Make it visual: people buy with their eyes. Show the value clearly.',
              text_zh: '📸 重要（模板）：创建预览内容 - 截图：5-7 张高质量图片显示不同部分。视频演练：2-3 分钟的 Loom 视频显示如何使用它。前后示例：显示空模板与填写版本。功能亮点：列出关键功能和好处。使其可视化：人们用眼睛购买。清楚地展示价值。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            {
              text: '📄 CRITICAL (Template): Set up sales page - Include: (1) Clear headline: "What problem does this solve?", (2) Preview images: show template in action, (3) Video demo: 2-3 minute walkthrough, (4) Instant download: emphasize immediate access, (5) What\'s included: list all files/components, (6) Testimonials: beta user feedback, (7) Pricing and CTA: "Buy Now" button. Platform handles checkout and delivery automatically.',
              text_zh: '📄 关键（模板）：设置销售页面 - 包括：(1) 清晰的标题："这解决了什么问题？"，(2) 预览图片：显示模板的实际应用，(3) 视频演示：2-3 分钟演练，(4) 即时下载：强调即时访问，(5) 包含内容：列出所有文件/组件，(6) 推荐：测试用户反馈，(7) 定价和 CTA："立即购买"按钮。平台自动处理结账和交付。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            // Membership Community Creation Path
            {
              text: '👥 OPTION C: CREATE MEMBERSHIP COMMUNITY - Best if you provide ongoing support, accountability, or exclusive content',
              text_zh: '👥 选项 C：创建会员社区 - 如果您提供持续支持、问责或独家内容，最佳选择',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context'
            },
            {
              text: '🏠 CRITICAL (Community): Choose platform - Western: Discord (free, great for engagement), Circle (community-focused, $89/mo), Mighty Networks (all-in-one, $41/mo), Patreon (creator-friendly, 5-12% fee). Chinese: 微信群 (free, but limited to 500 members), 知识星球 (community platform, ¥3000/year), QQ群 (free, good for younger audience), 小鹅通社群 (integrated with courses, ¥4800/year). Choose based on: features needed, budget, where your audience hangs out. Start simple: Discord or WeChat for MVP.',
              text_zh: '🏠 关键（社区）：选择平台 - 西方：Discord（免费，非常适合参与），Circle（以社区为中心，$89/月），Mighty Networks（一体化，$41/月），Patreon（对创作者友好，5-12% 费用）。中国：微信群（免费，但限制为 500 名成员），知识星球（社区平台，¥3000/年），QQ群（免费，适合年轻受众），小鹅通社群（与课程集成，¥4800/年）。根据以下选择：所需功能、预算、您的受众在哪里闲逛。从简单开始：Discord 或微信用于 MVP。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical'
            },
            {
              text: '🏗️ CRITICAL (Community): Set up basic structure - Create 3-5 channels/sections: (1) Welcome channel: rules, intro, how to get started, (2) Intro channel: members introduce themselves, (3) Main discussion: general questions and conversations, (4) Resources: templates, guides, links, (5) Wins/celebrations: members share successes. Keep it simple for MVP. Add more channels based on member needs later. Pin important messages in each channel.',
              text_zh: '🏗️ 关键（社区）：设置基本结构 - 创建 3-5 个频道/部分：(1) 欢迎频道：规则、介绍、如何开始，(2) 介绍频道：成员介绍自己，(3) 主要讨论：一般问题和对话，(4) 资源：模板、指南、链接，(5) 胜利/庆祝：成员分享成功。MVP 保持简单。稍后根据成员需求添加更多频道。在每个频道中置顶重要消息。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '👋 IMPORTANT (Community): Create onboarding flow - Welcome message: Auto-send when someone joins, explain community purpose and rules. Intro prompt: Ask new members to introduce themselves (name, goal, biggest challenge). Guidelines: Clear rules (be respectful, no spam, stay on topic). First action: Give them something to do immediately (complete profile, post intro, access first resource). Make them feel welcomed and valued from day 1.',
              text_zh: '👋 重要（社区）：创建入职流程 - 欢迎消息：有人加入时自动发送，解释社区目的和规则。介绍提示：要求新成员介绍自己（姓名、目标、最大挑战）。指南：明确规则（尊重、无垃圾邮件、保持主题）。第一个操作：立即给他们一些事情做（完成个人资料、发布介绍、访问第一个资源）。从第 1 天起让他们感到受欢迎和重视。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            {
              text: '📅 IMPORTANT (Community): Plan engagement strategy - Weekly Q&A: Live session or thread where you answer questions (1 hour/week). Daily prompts: Post discussion questions or challenges to spark conversation. Member spotlights: Feature successful members weekly to build community. Content drops: Share exclusive tips, resources, or behind-the-scenes content 2-3x/week. Consistency is key: show up regularly to build habit and trust.',
              text_zh: '📅 重要（社区）：规划参与策略 - 每周问答：您回答问题的直播会话或主题（1 小时/周）。每日提示：发布讨论问题或挑战以激发对话。成员聚焦：每周展示成功的成员以建立社区。内容投放：每周 2-3 次分享独家技巧、资源或幕后内容。一致性是关键：定期出现以建立习惯和信任。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            {
              text: '💳 IMPORTANT (Community): Set up payment integration - Patreon: Built-in payment processing, easy setup. Stripe: Connect to Discord/Circle via Memberful or MemberSpace. Membership bot: Use bots like MEE6 (Discord) to manage paid roles. Manual invite: Collect payment via PayPal/Venmo, manually add to community (works for MVP). Choose based on: platform, member count, automation needs. Start manual if <20 members, automate when you scale.',
              text_zh: '💳 重要（社区）：设置付款集成 - Patreon：内置付款处理，易于设置。Stripe：通过 Memberful 或 MemberSpace 连接到 Discord/Circle。会员机器人：使用 MEE6（Discord）等机器人管理付费角色。手动邀请：通过 PayPal/Venmo 收取付款，手动添加到社区（适用于 MVP）。根据以下选择：平台、成员数量、自动化需求。如果 <20 名成员，从手动开始，扩展时自动化。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            // Digital Product Creation Path
            {
              text: '📦 OPTION D: CREATE DIGITAL PRODUCT - Best for one-time deliverables like ebooks, guides, presets, or worksheets',
              text_zh: '📦 选项 D：创建数字产品 - 最适合一次性交付物，如电子书、指南、预设或工作表',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context'
            },
            {
              text: '📝 CRITICAL (Digital Product): Choose product format - Ebook/Guide: Comprehensive written content (20-50 pages). Presets: Lightroom, Photoshop, or video editing presets. Templates: Canva, PowerPoint, or design templates. Worksheets: PDF workbooks, planners, or trackers. Checklists: Step-by-step action lists. Choose based on: your expertise, audience needs, creation time. Example: Fitness influencer → Meal plan guide. Design influencer → Canva template pack.',
              text_zh: '📝 关键（数字产品）：选择产品格式 - 电子书/指南：全面的书面内容（20-50 页）。预设：Lightroom、Photoshop 或视频编辑预设。模板：Canva、PowerPoint 或设计模板。工作表：PDF 工作簿、规划器或跟踪器。清单：逐步操作列表。根据以下选择：您的专业知识、受众需求、创建时间。示例：健身网红 → 膳食计划指南。设计网红 → Canva 模板包。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical'
            },
            {
              text: '✂️ CRITICAL (Digital Product): Create MVP version - Start small: 10-page guide beats 50-page guide that never launches. Focus on core value: What\'s the ONE transformation this delivers? Cut everything else. Example: "7-Day Meal Plan" not "Complete Nutrition System". Use existing content: Repurpose your best posts, videos, or client work. Tools: Canva (design), Google Docs (writing), Notion (organization). Aim for 80% quality, not 100%. Ship it, then improve based on feedback.',
              text_zh: '✂️ 关键（数字产品）：创建 MVP 版本 - 从小开始：10 页指南胜过永远不会推出的 50 页指南。专注于核心价值：这提供了什么一个转型？删除其他所有内容。示例："7 天膳食计划"而不是"完整营养系统"。使用现有内容：重新利用您最好的帖子、视频或客户工作。工具：Canva（设计）、Google Docs（写作）、Notion（组织）。目标是 80% 的质量，而不是 100%。发布它，然后根据反馈改进。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '🎨 IMPORTANT (Digital Product): Design simple cover and branding - Cover design: Use Canva templates, keep it clean and professional. Consistent branding: Use your brand colors and fonts throughout. Professional but not over-produced: Clean layout, readable fonts, good contrast. Don\'t spend weeks on design. Tools: Canva (easiest), Figma (more control), Adobe (professional). Hire on Fiverr ($20-50) if design isn\'t your strength. First impression matters, but content matters more.',
              text_zh: '🎨 重要（数字产品）：设计简单的封面和品牌 - 封面设计：使用 Canva 模板，保持干净和专业。一致的品牌：在整个过程中使用您的品牌颜色和字体。专业但不过度制作：干净的布局、可读的字体、良好的对比度。不要在设计上花费数周时间。工具：Canva（最简单）、Figma（更多控制）、Adobe（专业）。如果设计不是您的强项，在 Fiverr 上雇用（$20-50）。第一印象很重要，但内容更重要。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            {
              text: '🏪 IMPORTANT (Digital Product): Choose distribution platform - Western: Gumroad (easiest, 10% fee, instant delivery), Stan Store (creator-focused, $29/mo), Beacons (link-in-bio + store, $10/mo), Own website (Carrd + Stripe, full control). Chinese: Gumroad (works in China), 抖音小店 (Douyin integration), 微信小商店 (WeChat ecosystem), 淘宝 (established marketplace). Choose based on: where your audience is, fees, ease of setup. Start with Gumroad for simplicity.',
              text_zh: '🏪 重要（数字产品）：选择分发平台 - 西方：Gumroad（最简单，10% 费用，即时交付），Stan Store（以创作者为中心，$29/月），Beacons（链接在简介 + 商店，$10/月），自己的网站（Carrd + Stripe，完全控制）。中国：Gumroad（在中国有效），抖音小店（抖音集成），微信小商店（微信生态系统），淘宝（成熟的市场）。根据以下选择：您的受众在哪里、费用、设置的便利性。从 Gumroad 开始以简化。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            {
              text: '📦 IMPORTANT (Digital Product): Set up delivery system - Instant download: Platform auto-sends file after purchase (Gumroad, Stan Store). Email delivery: Send PDF via email automation (Mailchimp, ConvertKit). Access portal: Create simple member area (Notion, Google Drive with link). Choose based on: file size, platform capabilities, user experience. Test the full flow: buy your own product, make sure delivery works smoothly. Backup plan: Have manual delivery ready in case automation fails.',
              text_zh: '📦 重要（数字产品）：设置交付系统 - 即时下载：平台在购买后自动发送文件（Gumroad、Stan Store）。电子邮件交付：通过电子邮件自动化发送 PDF（Mailchimp、ConvertKit）。访问门户：创建简单的会员区域（Notion、带链接的 Google Drive）。根据以下选择：文件大小、平台功能、用户体验。测试完整流程：购买您自己的产品，确保交付顺利进行。备份计划：准备好手动交付以防自动化失败。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            }
          ],
          dodCriteria: [
            'First product MVP completed and uploaded to platform',
            'Sales page/introduction materials created',
            'Preview content prepared (demo video or sample)',
            'Payment system integrated',
            'Product accessible via public link',
            'End-to-end purchase flow tested and working'
          ],
          dodCriteria_zh: [
            '第一个产品 MVP 已完成并上传到平台',
            '创建销售页面/介绍材料',
            '准备预览内容（演示视频或样本）',
            '集成付款系统',
            '产品可通过公共链接访问',
            '端到端购买流程已测试并正常工作'
          ],
          timeEstimateWeeks: 2,
          status: 'locked'
        };
      }

      // Step 2: Seed User Testing
      if (steps[2]) {
        steps[2] = {
          ...steps[2],
          title: 'Seed User Testing',
          title_zh: '种子用户测试',
          description: 'Have core followers pay to test, collect feedback, optimize product',
          description_zh: '让核心粉丝付费测试，收集反馈，优化产品',
          actions: [
            {
              text: '💡 WHY SEED TESTING MATTERS: Your core followers are your best beta testers. They trust you, want to support you, and will give honest feedback. Paid beta testing (even at discount) validates real demand and creates committed users who become your best advocates.',
              text_zh: '💡 为什么种子测试很重要：您的核心粉丝是您最好的测试用户。他们信任您，想要支持您，并会给出诚实的反馈。付费测试（即使打折）验证真实需求并创造承诺的用户，他们成为您最好的倡导者。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context'
            },
            {
              text: '👥 CRITICAL: Recruit seed users from core followers - Target: 20-50 seed users (scale based on your follower count: <10K followers = 20 users, 10K-50K = 30-40 users, 50K+ = 50+ users). 📊 DETAILED SCALING GUIDE: <5K followers = 15-20 users (0.3-0.4% of audience), 5K-10K = 20-30 users (0.3% of audience), 10K-30K = 30-50 users (0.2-0.3%), 30K-50K = 50-75 users (0.15-0.2%), 50K-100K = 75-150 users (0.1-0.15%), 100K+ = 150-300 users (0.1-0.15%). Higher follower counts can recruit more users but expect lower conversion rates. Recruitment channels: Western: Instagram Stories with "Join Beta" link, YouTube Community post, email list announcement, Twitter/X thread. Chinese: 小红书笔记 with beta signup, 抖音置顶评论, 微信朋友圈, 粉丝群公告. Offer: Founder pricing (50-70% off), exclusive access, direct line to you, influence product direction. Create simple signup form (Google Forms, Typeform, or 问卷星 for Chinese).',
              text_zh: '👥 关键：从核心粉丝招募种子用户 - 目标：20-50 个种子用户（根据您的粉丝数量调整：<10K 粉丝 = 20 个用户，10K-50K = 30-40 个用户，50K+ = 50+ 个用户）。📊 详细调整指南：<5K 粉丝 = 15-20 个用户（受众的 0.3-0.4%），5K-10K = 20-30 个用户（受众的 0.3%），10K-30K = 30-50 个用户（0.2-0.3%），30K-50K = 50-75 个用户（0.15-0.2%），50K-100K = 75-150 个用户（0.1-0.15%），100K+ = 150-300 个用户（0.1-0.15%）。粉丝数量越多可以招募更多用户，但预期转化率较低。招募渠道：西方：带有"加入测试版"链接的 Instagram Stories、YouTube Community 帖子、电子邮件列表公告、Twitter/X 主题。中国：带有测试版注册的小红书笔记、抖音置顶评论、微信朋友圈、粉丝群公告。提供：创始人定价（50-70% 折扣）、独家访问、与您的直接联系、影响产品方向。创建简单的注册表单（Google Forms、Typeform 或中国的问卷星）。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '🎁 IMPORTANT: Design testing benefits package - Founder pricing: 50-70% off regular price (e.g., $99 course → $29 founder price). Exclusive perks: Private community access, monthly Q&A calls, early access to updates, lifetime discount on future products. Recognition: Feature them as founding members, shout-outs in content. Support: Priority support, direct access to you via DM/email. Make them feel special and valued. Document benefits clearly in recruitment materials.',
              text_zh: '🎁 重要：设计测试福利包 - 创始人定价：比常规价格低 50-70%（例如，$99 课程 → $29 创始人价格）。独家福利：私人社区访问、每月问答电话、更新的早期访问、未来产品的终身折扣。认可：将他们作为创始成员展示，在内容中大声喊出。支持：优先支持，通过 DM/电子邮件直接访问您。让他们感到特别和有价值。在招募材料中清楚地记录福利。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            {
              text: '📋 CRITICAL: Set up feedback collection system - Feedback channels: Western: Typeform survey (structured feedback), Google Forms (simple), Private Discord/Slack channel (ongoing discussion), Email check-ins (personal touch), Zoom calls with top users (deep insights). Chinese: 问卷星 survey, 微信群 for discussion, 1-on-1 微信 conversations, 腾讯会议 video calls. Feedback questions: (1) "How satisfied are you with the product?" (1-10 scale), (2) "What did you like most?", (3) "What was confusing or frustrating?", (4) "What features are missing?", (5) "Would you recommend to others?" (NPS), (6) "What results did you achieve?". Schedule: Week 1 check-in, Week 2 mid-point survey, Week 4 final feedback. Make it easy to give feedback.',
              text_zh: '📋 关键：设置反馈收集系统 - 反馈渠道：西方：Typeform 调查（结构化反馈）、Google Forms（简单）、私人 Discord/Slack 频道（持续讨论）、电子邮件检查（个人接触）、与顶级用户的 Zoom 通话（深入见解）。中国：问卷星调查、微信群讨论、1 对 1 微信对话、腾讯会议视频通话。反馈问题：(1) "您对产品的满意度如何？"（1-10 分），(2) "您最喜欢什么？"，(3) "什么令人困惑或沮丧？"，(4) "缺少什么功能？"，(5) "您会向其他人推荐吗？"（NPS），(6) "您取得了什么结果？"。时间表：第 1 周检查、第 2 周中期调查、第 4 周最终反馈。使反馈变得容易。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '🚀 IMPORTANT: Launch beta to seed users - Onboarding: Send welcome email/message within 1 hour of purchase, provide clear access instructions, set expectations (beta = bugs expected, feedback needed), invite to feedback channel. First week: Check in personally with each user, ask if they need help, encourage them to start using product, remind them to share feedback. Engagement: Post in feedback channel 2-3x per week, share updates and improvements, celebrate user wins, respond to questions within 24 hours. Make them feel like VIPs.',
              text_zh: '🚀 重要：向种子用户发布测试版 - 入职：在购买后 1 小时内发送欢迎电子邮件/消息，提供清晰的访问说明，设定期望（测试版 = 预期有错误，需要反馈），邀请到反馈渠道。第一周：亲自与每个用户联系，询问他们是否需要帮助，鼓励他们开始使用产品，提醒他们分享反馈。参与：每周在反馈渠道发布 2-3 次，分享更新和改进，庆祝用户成功，在 24 小时内回复问题。让他们感觉像 VIP。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            },
            {
              text: '📸 CRITICAL: Collect testimonial materials - Types of testimonials: Written reviews: Short quotes (1-2 sentences) for sales page, Screenshots: User results, progress, wins (Instagram Stories, 小红书笔记), Video testimonials: 30-60 second clips (most powerful), Usage examples: Before/after, case studies, transformation stories. Collection strategy: Western: Ask via email with template, request Instagram Story tags, schedule Zoom for video testimonials. Chinese: Request 小红书笔记 or 抖音视频, collect 微信截图, ask for video testimonials via 微信视频通话. Timing: Ask after they achieve a result (Week 2-3), make it easy with templates/prompts. Target: ≥10 high-quality testimonials. Get permission to use publicly.',
              text_zh: '📸 关键：收集推荐材料 - 推荐类型：书面评论：销售页面的简短引用（1-2 句），截图：用户结果、进度、成功（Instagram Stories、小红书笔记），视频推荐：30-60 秒片段（最强大），使用示例：前后对比、案例研究、转型故事。收集策略：西方：通过带模板的电子邮件询问，请求 Instagram Story 标签，安排 Zoom 进行视频推荐。中国：请求小红书笔记或抖音视频，收集微信截图，通过微信视频通话询问视频推荐。时机：在他们取得结果后询问（第 2-3 周），使用模板/提示使其变得容易。目标：≥10 个高质量推荐。获得公开使用的许可。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '🔍 IMPORTANT: Analyze feedback and optimize product - Feedback analysis: Categorize feedback: bugs, feature requests, UX issues, content gaps. Identify patterns: if 3+ users mention same issue, it\'s critical. Calculate metrics: completion rate (% who finish course/use template), satisfaction score (average rating), NPS (% promoters - % detractors). Prioritize fixes: Critical bugs (fix immediately), High-impact improvements (add in Week 3-4), Nice-to-haves (backlog). Optimization actions: Fix critical bugs within 48 hours, improve confusing sections, add missing content/features, enhance onboarding based on questions. Communicate changes: Update users on improvements, show you\'re listening, thank them for feedback. Target: ≥60% completion rate, ≥8/10 satisfaction.',
              text_zh: '🔍 重要：分析反馈并优化产品 - 反馈分析：对反馈进行分类：错误、功能请求、UX 问题、内容差距。识别模式：如果 3+ 个用户提到相同问题，则为关键。计算指标：完成率（完成课程/使用模板的百分比）、满意度分数（平均评分）、NPS（推荐者百分比 - 批评者百分比）。优先修复：关键错误（立即修复）、高影响改进（在第 3-4 周添加）、锦上添花（待办事项）。优化操作：在 48 小时内修复关键错误，改进令人困惑的部分，添加缺失的内容/功能，根据问题增强入职。沟通变化：向用户更新改进，表明您在倾听，感谢他们的反馈。目标：≥60% 完成率，≥8/10 满意度。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important'
            },
            {
              text: '📊 CRITICAL: Calculate key metrics - Metrics to track: (1) Completion rate: % of users who complete product (course/template/community engagement). Target: ≥60% for courses, ≥70% for templates, ≥50% active for communities. (2) User satisfaction: Average rating (1-10 scale). Target: ≥8/10. (3) Net Promoter Score (NPS): "How likely to recommend?" (0-10). Calculate: % promoters (9-10) - % detractors (0-6). Target: ≥40. (4) Time to value: How long until users see results? Target: <1 week. (5) Support requests: Number of questions/issues. Lower is better. Tools: Spreadsheet tracking, survey tools (Typeform, 问卷星), product analytics (if applicable). Review metrics weekly, identify trends, adjust product accordingly.',
              text_zh: '📊 关键：计算关键指标 - 要跟踪的指标：(1) 完成率：完成产品的用户百分比（课程/模板/社区参与）。目标：课程 ≥60%，模板 ≥70%，社区 ≥50% 活跃。(2) 用户满意度：平均评分（1-10 分）。目标：≥8/10。(3) 净推荐值（NPS）："推荐的可能性有多大？"（0-10）。计算：推荐者百分比（9-10）- 批评者百分比（0-6）。目标：≥40。(4) 价值实现时间：用户看到结果需要多长时间？目标：<1 周。(5) 支持请求：问题/问题数量。越低越好。工具：电子表格跟踪、调查工具（Typeform、问卷星）、产品分析（如果适用）。每周审查指标，识别趋势，相应调整产品。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            }
          ],
          dodCriteria: [
            '20-50 seed users recruited and onboarded (scale: <5K followers=15-20, 5K-10K=20-30, 10K-30K=30-50, 30K-50K=50-75, 50K-100K=75-150, 100K+=150-300)',
            'Product completion rate ≥60%',
            'User satisfaction ≥8/10',
            '≥10 high-quality user testimonials collected (scale: <10K followers=10+, 10K-50K=15+, 50K+=20+)',
            'Product optimized based on first-round feedback',
            'Net Promoter Score (NPS) ≥40',
            'Feedback collection system active and used',
            'Key metrics tracked and documented'
          ],
          dodCriteria_zh: [
            '招募并引导 20-50 个种子用户（按规模调整：<5K 粉丝=15-20，5K-10K=20-30，10K-30K=30-50，30K-50K=50-75，50K-100K=75-150，100K+=150-300）',
            '产品完成率 ≥60%',
            '用户满意度 ≥8/10',
            '收集 ≥10 个高质量用户推荐（按规模调整：<10K 粉丝=10+，10K-50K=15+，50K+=20+）',
            '根据第一轮反馈优化产品',
            '净推荐值（NPS）≥40',
            '反馈收集系统活跃并使用',
            '跟踪并记录关键指标'
          ],
          timeEstimateWeeks: 4,
          status: 'locked'
        };
      }

      // Step 3: Launch and Scale
      if (steps[3]) {
        steps[3] = {
          ...steps[3],
          title: 'Launch and Scale',
          title_zh: '发布和扩展',
          description: 'Achieve large-scale product sales through content matrix + live streams + private channels',
          description_zh: '通过内容矩阵 + 直播 + 私域渠道实现大规模产品销售',
          actions: [
            {
              text: '💡 WHY LAUNCH STRATEGY MATTERS: Random posting doesn\'t create sales momentum. A structured launch with warm-up, launch, and scarcity phases converts 5-10x better than "always available" products. Your audience needs urgency and social proof to take action.',
              text_zh: '💡 为什么发布策略很重要：随机发布不会创造销售动力。具有预热、发布和稀缺性阶段的结构化发布比"始终可用"的产品转化率高 5-10 倍。您的受众需要紧迫感和社会证明才能采取行动。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context'
            },
            {
              text: '📅 CRITICAL: Plan launch rhythm (3 phases) - Phase 1: Warm-up (3-5 days): Tease product, share behind-the-scenes, build anticipation, collect waitlist. Target waitlist size by follower count: <5K=50-100, 5K-10K=100-200, 10K-30K=200-500, 30K-50K=500-1000, 50K-100K=1000-2000, 100K+=2000+. Phase 2: Launch (7-10 days): Open cart, early bird pricing, heavy promotion, daily content, live streams. Phase 3: Scarcity (2-3 days): Last chance messaging, countdown timers, bonuses expiring, final push. Total launch window: 12-18 days. Document timeline: specific dates for each phase, content schedule, promotional activities. Western approach: Email-heavy with social support. Chinese approach: 微信朋友圈 + 直播 heavy.',
              text_zh: '📅 关键：规划发布节奏（3 个阶段）- 阶段 1：预热（3-5 天）：预告产品、分享幕后花絮、建立期待、收集等待名单。按粉丝数量的目标等待名单规模：<5K=50-100，5K-10K=100-200，10K-30K=200-500，30K-50K=500-1000，50K-100K=1000-2000，100K+=2000+。阶段 2：发布（7-10 天）：开放购买、早鸟定价、大力推广、每日内容、直播。阶段 3：稀缺性（2-3 天）：最后机会信息、倒计时、奖励到期、最后冲刺。总发布窗口：12-18 天。记录时间表：每个阶段的具体日期、内容时间表、促销活动。西方方法：以电子邮件为主，社交支持。中国方法：微信朋友圈 + 直播为主。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '🎬 CRITICAL: Create content matrix for launch - Content types: (1) Short videos (15-60 seconds): Product teasers, testimonials, quick tips, behind-the-scenes. Create 20-30 videos. (2) Live streams: Product walkthrough, Q&A sessions, exclusive bonuses reveal. Schedule 3-5 lives during launch. (3) Private channel posts: Exclusive updates, early access offers, personal stories. Post daily in 微信朋友圈 (Chinese) or Instagram Stories (Western). Distribution: Western: Instagram Reels, TikTok, YouTube Shorts, Instagram Stories, email. Chinese: 抖音, 小红书, 视频号, 微信朋友圈, 微信群. Batch create content: film all videos in 2-3 days, schedule throughout launch. Focus: transformation stories, social proof, urgency.',
              text_zh: '🎬 关键：为发布创建内容矩阵 - 内容类型：(1) 短视频（15-60 秒）：产品预告、推荐、快速技巧、幕后花絮。创建 20-30 个视频。(2) 直播：产品演练、问答环节、独家奖励揭示。在发布期间安排 3-5 次直播。(3) 私域渠道帖子：独家更新、早期访问优惠、个人故事。在微信朋友圈（中国）或 Instagram Stories（西方）每天发布。分发：西方：Instagram Reels、TikTok、YouTube Shorts、Instagram Stories、电子邮件。中国：抖音、小红书、视频号、微信朋友圈、微信群。批量创建内容：在 2-3 天内拍摄所有视频，在整个发布期间安排。重点：转型故事、社会证明、紧迫感。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '💰 CRITICAL: Set up launch tactics and offers - Tiered pricing: Early bird (first 24-48 hours, 30-40% off), Regular launch price (days 3-10), Last chance (final 2-3 days, add bonuses). Bonuses: Add complementary resources (templates, checklists, bonus modules) that expire with launch. Group discounts: "Buy with 2 friends, get 20% off" (works great in Chinese market with 拼团). Affiliate program: Offer 20-30% commission to partners who promote. Payment plans: Offer 3-6 month installments for products >$200. Guarantee: 30-day money-back guarantee to reduce risk. Document all offers with specific terms, expiration dates, and promotional copy.',
              text_zh: '💰 关键：设置发布策略和优惠 - 分层定价：早鸟（前 24-48 小时，30-40% 折扣），常规发布价格（第 3-10 天），最后机会（最后 2-3 天，添加奖励）。奖励：添加互补资源（模板、清单、奖励模块），随发布到期。团购折扣："与 2 个朋友一起购买，享受 20% 折扣"（在中国市场的拼团中效果很好）。联盟计划：向推广的合作伙伴提供 20-30% 的佣金。付款计划：为 >$200 的产品提供 3-6 个月的分期付款。保证：30 天退款保证以降低风险。记录所有优惠的具体条款、到期日期和促销文案。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: '🔥 PHASE 1: WARM-UP (Days 1-5) - Build anticipation and collect waitlist',
              text_zh: '🔥 阶段 1：预热（第 1-5 天）- 建立期待并收集等待名单',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'warmup'
            },
            {
              text: '📢 CRITICAL: Execute warm-up campaign - Day 1-2: Tease announcement ("I\'ve been working on something special for you..."), share behind-the-scenes of creation process, hint at transformation it provides. Day 3-4: Reveal product name and core benefit, share beta user testimonials, open waitlist signup (offer early bird discount for waitlist members). Day 5: Final teaser with launch date announcement, share preview/demo, remind waitlist to set reminders. Content: 2-3 posts per day across all platforms, 1 email to list (if you have one), daily 微信朋友圈 updates (Chinese) or Instagram Stories (Western). Goal: Build waitlist of 100-200 people (scale based on follower count).',
              text_zh: '📢 关键：执行预热活动 - 第 1-2 天：预告公告（"我一直在为您准备一些特别的东西..."），分享创作过程的幕后花絮，暗示它提供的转型。第 3-4 天：揭示产品名称和核心好处，分享测试用户推荐，开放等待名单注册（为等待名单成员提供早鸟折扣）。第 5 天：最终预告，宣布发布日期，分享预览/演示，提醒等待名单设置提醒。内容：每天在所有平台上发布 2-3 个帖子，向列表发送 1 封电子邮件（如果有），每天更新微信朋友圈（中国）或 Instagram Stories（西方）。目标：建立 100-200 人的等待名单（根据粉丝数量调整）。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical',
              phase: 'warmup'
            },
            {
              text: '📧 IMPORTANT: Set up launch email/message sequences - Western: Email sequence (5-7 emails): Launch announcement, Product benefits, Testimonials, Early bird reminder, FAQ, Last chance. Chinese: 微信 message sequence: Launch announcement in 朋友圈, Product details in 公众号 article, Testimonials in 小红书/抖音, Reminders in 微信群, Last chance in 朋友圈. Schedule emails/messages in advance using tools: Mailchimp, ConvertKit (Western) or 微信公众号, 企业微信 (Chinese). Personalize where possible, use urgency and scarcity language.',
              text_zh: '📧 重要：设置发布电子邮件/消息序列 - 西方：电子邮件序列（5-7 封电子邮件）：发布公告、产品好处、推荐、早鸟提醒、常见问题、最后机会。中国：微信消息序列：朋友圈发布公告、公众号文章产品详情、小红书/抖音推荐、微信群提醒、朋友圈最后机会。使用工具提前安排电子邮件/消息：Mailchimp、ConvertKit（西方）或微信公众号、企业微信（中国）。尽可能个性化，使用紧迫感和稀缺性语言。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important',
              phase: 'warmup'
            },
            {
              text: '🚀 PHASE 2: LAUNCH (Days 6-15) - Open cart and drive sales',
              text_zh: '🚀 阶段 2：发布（第 6-15 天）- 开放购买并推动销售',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'launch'
            },
            {
              text: '🎉 CRITICAL: Execute launch day - Launch at specific time (announce in advance): 9am PT (Western) or 8pm Beijing time (Chinese, after work). Launch announcement: Post across all platforms simultaneously, send email to waitlist, post in 微信群/朋友圈, go live on Instagram/抖音 to announce. Early bird offer: First 24-48 hours get 30-40% off, display countdown timer on sales page, remind followers every 6-8 hours. Engagement: Respond to every comment and DM within 1 hour, answer questions publicly to build social proof, share purchase notifications ("10 people just joined!"). Live stream: Host 1-2 hour live Q&A on launch day, walk through product, answer objections, offer exclusive live-only bonus. Goal: 30-50% of total sales happen in first 48 hours.',
              text_zh: '🎉 关键：执行发布日 - 在特定时间发布（提前宣布）：太平洋时间上午 9 点（西方）或北京时间晚上 8 点（中国，下班后）。发布公告：同时在所有平台上发布，向等待名单发送电子邮件，在微信群/朋友圈发布，在 Instagram/抖音上直播宣布。早鸟优惠：前 24-48 小时享受 30-40% 折扣，在销售页面上显示倒计时，每 6-8 小时提醒粉丝一次。参与：在 1 小时内回复每条评论和私信，公开回答问题以建立社会证明，分享购买通知（"刚刚有 10 人加入！"）。直播：在发布日主持 1-2 小时的直播问答，演练产品，回答异议，提供独家直播专属奖励。目标：前 48 小时内完成总销售额的 30-50%。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 20,
              priority: 'critical',
              phase: 'launch'
            },
            {
              text: '📱 CRITICAL: Maintain daily launch momentum (Days 3-10) - Daily content: Post 2-3 times per day: customer testimonials, product demos, FAQ answers, behind-the-scenes, urgency reminders. Live streams: Host 2-3 additional lives during launch week: product walkthrough, success stories, bonus reveals, Q&A sessions. Western: Instagram/YouTube/TikTok lives. Chinese: 抖音/视频号/小红书 直播. Private channels: Daily updates in 微信朋友圈 (Chinese) or Instagram Stories (Western), share customer wins and testimonials, countdown to offer expiration. Email/微信: Send 1 email or 微信 message every 2-3 days with different angles: benefits, testimonials, FAQ, urgency. Social proof: Share purchase notifications, customer results, testimonial screenshots. Keep momentum high with consistent presence.',
              text_zh: '📱 关键：保持每日发布动力（第 3-10 天）- 每日内容：每天发布 2-3 次：客户推荐、产品演示、常见问题解答、幕后花絮、紧迫性提醒。直播：在发布周期间主持 2-3 次额外的直播：产品演练、成功故事、奖励揭示、问答环节。西方：Instagram/YouTube/TikTok 直播。中国：抖音/视频号/小红书直播。私域渠道：在微信朋友圈（中国）或 Instagram Stories（西方）每天更新，分享客户成功和推荐，倒计时优惠到期。电子邮件/微信：每 2-3 天发送 1 封电子邮件或微信消息，从不同角度：好处、推荐、常见问题、紧迫性。社会证明：分享购买通知、客户结果、推荐截图。通过持续存在保持高动力。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical',
              phase: 'launch'
            },
            {
              text: '🎁 IMPORTANT: Leverage launch-specific tactics - Western tactics: Email campaigns (send 5-7 emails during launch), Instagram Stories with swipe-up links, YouTube Community posts, affiliate partnerships (give partners 20-30% commission), Product Hunt launch (if applicable), podcast interviews or guest posts. Chinese tactics: 直播带货 (live streaming commerce): Host daily or every-other-day lives, demonstrate product, offer live-exclusive bonuses, use countdown timers. 朋友圈 (WeChat Moments): Post 2-3x daily with customer stories, urgency messaging, social proof. 拼团 (group buying): "Get 3 friends, everyone gets 20% off", creates viral sharing. 分销 (distribution network): Recruit customers as distributors, offer 20-30% commission. 微信群 (WeChat groups): Post daily updates, answer questions, create FOMO with purchase notifications.',
              text_zh: '🎁 重要：利用发布特定策略 - 西方策略：电子邮件活动（在发布期间发送 5-7 封电子邮件），带有向上滑动链接的 Instagram Stories，YouTube Community 帖子，联盟合作伙伴关系（给合作伙伴 20-30% 的佣金），Product Hunt 发布（如果适用），播客访谈或客座帖子。中国策略：直播带货：每天或每隔一天主持直播，演示产品，提供直播专属奖励，使用倒计时。朋友圈：每天发布 2-3 次客户故事、紧迫性信息、社会证明。拼团：" 3 个朋友一起，每个人都享受 20% 折扣"，创造病毒式分享。分销：招募客户作为分销商，提供 20-30% 的佣金。微信群：每天发布更新，回答问题，通过购买通知创造 FOMO。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'important',
              phase: 'launch'
            },
            {
              text: '⏰ PHASE 3: SCARCITY (Days 11-15) - Final push with urgency',
              text_zh: '⏰ 阶段 3：稀缺性（第 11-15 天）- 最后冲刺，制造紧迫感',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'context',
              phase: 'scarcity'
            },
            {
              text: '🔥 CRITICAL: Execute scarcity phase - Day 11-12: Announce cart closing in 3 days, remind about bonuses expiring, share final testimonials, increase posting frequency to 3-4x per day. Day 13: "48 hours left" messaging, countdown timers everywhere, final live stream with exclusive bonus for attendees, email/微信 reminder. Day 14: "24 hours left" - hourly reminders on Stories/朋友圈, final email/微信 blast, share purchase count ("127 people joined!"), last chance for bonuses. Day 15: "Final hours" - post every 2-3 hours, countdown timer on sales page, final live stream or Stories takeover, close cart at announced time. Messaging: Focus on FOMO (fear of missing out), emphasize what they\'ll miss (bonuses, discount, transformation), use countdown timers visually, share social proof (purchase notifications, testimonials). Goal: 20-30% of total sales happen in final 3 days.',
              text_zh: '🔥 关键：执行稀缺性阶段 - 第 11-12 天：宣布 3 天后关闭购买，提醒奖励即将到期，分享最终推荐，将发布频率增加到每天 3-4 次。第 13 天："还剩 48 小时"信息，到处都是倒计时，最后一次直播，为参与者提供独家奖励，电子邮件/微信提醒。第 14 天："还剩 24 小时" - 在 Stories/朋友圈每小时提醒一次，最终电子邮件/微信群发，分享购买数量（"127 人加入！"），奖励的最后机会。第 15 天："最后几小时" - 每 2-3 小时发布一次，销售页面上的倒计时，最后一次直播或 Stories 接管，在宣布的时间关闭购买。信息：专注于 FOMO（害怕错过），强调他们会错过什么（奖励、折扣、转型），视觉上使用倒计时，分享社会证明（购买通知、推荐）。目标：最后 3 天内完成总销售额的 20-30%。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 20,
              priority: 'critical',
              phase: 'scarcity'
            },
            {
              text: '📊 CRITICAL: Monitor and optimize conversion during launch - Daily metrics tracking: Sales count and revenue, Conversion rate (visitors → buyers), Traffic sources (which content drives sales), Email/微信 open and click rates, Live stream attendance and conversion, Social media engagement (comments, shares, saves). Optimization actions: If conversion is low (<2%), improve sales page (add testimonials, clarify benefits, address objections), If traffic is low, increase content frequency and paid promotion, If live streams convert well, add more lives, If certain content performs well, create more of that type. Tools: Google Analytics, platform analytics (Instagram Insights, 抖音数据中心), payment platform dashboard (Stripe, Gumroad, 小鹅通). Review metrics daily, adjust strategy in real-time. Target: ≥3% conversion rate (visitors to buyers).',
              text_zh: '📊 关键：在发布期间监控和优化转化 - 每日指标跟踪：销售数量和收入，转化率（访客 → 买家），流量来源（哪些内容推动销售），电子邮件/微信打开率和点击率，直播出席率和转化率，社交媒体参与度（评论、分享、收藏）。优化操作：如果转化率低（<2%），改进销售页面（添加推荐、澄清好处、解决异议），如果流量低，增加内容频率和付费推广，如果直播转化良好，添加更多直播，如果某些内容表现良好，创建更多该类型的内容。工具：Google Analytics、平台分析（Instagram Insights、抖音数据中心）、付款平台仪表板（Stripe、Gumroad、小鹅通）。每天审查指标，实时调整策略。目标：≥3% 转化率（访客到买家）。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical',
              phase: 'scarcity'
            },
            {
              text: '💰 CRITICAL: Calculate revenue and plan next steps - Post-launch analysis (Day 16-18): Total sales and revenue, Conversion rate by traffic source, Best performing content types, Customer feedback and satisfaction, Lessons learned for next launch. 📊 REVENUE PROJECTIONS BY AUDIENCE SIZE: <5K followers: 25-50 sales, $750-$2,500 revenue (0.5-1% conversion). 5K-10K: 50-100 sales, $1,500-$5,000 revenue (0.5-1% conversion). 10K-30K: 100-300 sales, $5,000-$15,000 revenue (0.5-1% conversion). 30K-50K: 200-400 sales, $10,000-$20,000 revenue (0.4-0.8% conversion). 50K-100K: 300-700 sales, $15,000-$35,000 revenue (0.3-0.7% conversion). 100K-300K: 500-1500 sales, $25,000-$75,000 revenue (0.3-0.5% conversion). 300K+: 1000+ sales, $50,000+ revenue (0.3-0.5% conversion). Note: Conversion rates typically decrease as audience size grows. Pricing assumptions: $30-50 for templates/digital products, $50-150 for courses, $20-50/month for communities. Adjust based on your actual pricing. Next product planning: Survey customers: "What do you want next?", Identify gaps in current product, Plan next launch in 2-3 months, Consider product ladder (low → mid → high ticket). Celebrate wins: Thank customers publicly, Share success metrics with audience, Reward top affiliates/partners.',
              text_zh: '💰 关键：计算收入并规划下一步 - 发布后分析（第 16-18 天）：总销售额和收入，按流量来源的转化率，表现最佳的内容类型，客户反馈和满意度，下次发布的经验教训。📊 按受众规模的收入预测：<5K 粉丝：25-50 次销售，$750-$2,500 收入（0.5-1% 转化率）。5K-10K：50-100 次销售，$1,500-$5,000 收入（0.5-1% 转化率）。10K-30K：100-300 次销售，$5,000-$15,000 收入（0.5-1% 转化率）。30K-50K：200-400 次销售，$10,000-$20,000 收入（0.4-0.8% 转化率）。50K-100K：300-700 次销售，$15,000-$35,000 收入（0.3-0.7% 转化率）。100K-300K：500-1500 次销售，$25,000-$75,000 收入（0.3-0.5% 转化率）。300K+：1000+ 次销售，$50,000+ 收入（0.3-0.5% 转化率）。注意：随着受众规模的增长，转化率通常会降低。定价假设：模板/数字产品 $30-50，课程 $50-150，社区 $20-50/月。根据您的实际定价调整。下一个产品规划：调查客户："您接下来想要什么？"，识别当前产品的差距，计划 2-3 个月后的下一次发布，考虑产品阶梯（低 → 中 → 高票）。庆祝胜利：公开感谢客户，与受众分享成功指标，奖励顶级联盟/合作伙伴。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 20,
              priority: 'critical',
              phase: 'scarcity'
            }
          ],
          dodCriteria: [
            'Launch campaign materials created (20-30 videos, 5-7 emails/messages)',
            'Content matrix executed across all platforms (videos + lives + posts)',
            '3-phase launch rhythm completed (warm-up, launch, scarcity)',
            'Launch tactics implemented (tiered pricing, bonuses, group discounts)',
            '≥100 products sold (scale: <5K followers=25+, 5K-10K=50+, 10K-30K=100+, 30K-50K=200+, 50K-100K=300+, 100K-300K=500+, 300K+=1000+)',
            'Revenue target achieved: <5K=$750+, 5K-10K=$1,500+, 10K-30K=$5,000+, 30K-50K=$10,000+, 50K-100K=$15,000+, 100K-300K=$25,000+, 300K+=$50,000+ (adjust for your pricing)',
            'Post-launch analysis completed with learnings documented',
            'Conversion rate ≥0.3-1% achieved (varies by audience size: smaller audiences convert higher)'
          ],
          dodCriteria_zh: [
            '创建发布活动材料（20-30 个视频，5-7 封电子邮件/消息）',
            '在所有平台上执行内容矩阵（视频 + 直播 + 帖子）',
            '完成 3 阶段发布节奏（预热、发布、稀缺性）',
            '实施发布策略（分层定价、奖励、团购折扣）',
            '≥100 个产品售出（按规模调整：<5K 粉丝=25+，5K-10K=50+，10K-30K=100+，30K-50K=200+，50K-100K=300+，100K-300K=500+，300K+=1000+）',
            '实现收入目标：<5K=$750+，5K-10K=$1,500+，10K-30K=$5,000+，30K-50K=$10,000+，50K-100K=$15,000+，100K-300K=$25,000+，300K+=$50,000+（根据您的定价调整）',
            '完成发布后分析并记录经验教训',
            '实现转化率 ≥0.3-1%（因受众规模而异：较小的受众转化率较高）'
          ],
          timeEstimateWeeks: 3,
          status: 'locked'
        };
      }

      return steps;
    }

    if ((stage === 'In Progress' || stage === 'Beginner') && steps[0]) {
      steps[0] = {
        ...steps[0],
        title: 'Identify Scalable Product',
        title_zh: '识别可扩展产品',
        description: 'Audit your services and choose the best product format to build',
        description_zh: '审计您的服务并选择最佳产品格式来构建',
        actions: [
          {
            text: '💡 WHY PRODUCTIZATION: Service businesses hit a revenue ceiling because time is limited. Products scale infinitely without your time. One course can serve 1,000 students. One template can help 10,000 clients.',
            text_zh: '💡 为什么产品化：服务业务会遇到收入上限，因为时间有限。产品可以无限扩展而不需要您的时间。一门课程可以服务 1,000 名学生。一个模板可以帮助 10,000 个客户。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'context'
          },
          {
            text: '📋 CRITICAL: Audit your service offerings - List all services you provide. For each, identify: (1) Most requested, (2) Highest margin, (3) Most repeatable. Rate productization potential (High/Medium/Low) based on standardization, scalability, and market demand. Select top 2-3 services to productize.',
            text_zh: '📋 关键：审计您的服务产品 - 列出您提供的所有服务。对于每个服务，识别：(1) 最受欢迎的，(2) 利润最高的，(3) 最可重复的。根据标准化、可扩展性和市场需求评估产品化潜力（高/中/低）。选择前 2-3 个服务进行产品化。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'critical'
          },
          {
            text: '📊 CRITICAL: Survey existing clients about productization',
            text_zh: '📊 关键：调查现有客户关于产品化的意见',
            type: 'survey',
            pointsCost: -5,
            pointsReward: 15,
            priority: 'critical'
          },
          {
            text: '🎯 CRITICAL: Choose product format - Survey clients: "What format would help you most?" (course, template, software, community). Research competitors: What formats are they using? Match format to your strengths: teaching → course, process → template, technical → software. Select ONE format to start.',
            text_zh: '🎯 关键：选择产品格式 - 调查客户："什么格式对您最有帮助？"（课程、模板、软件、社区）。研究竞争对手：他们使用什么格式？将格式与您的优势匹配：教学 → 课程，流程 → 模板，技术 → 软件。选择一种格式开始。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'critical'
          },
          {
            text: '💰 IMPORTANT: Research product pricing - Find 5-10 similar products in your niche. Document their pricing: tiers, features, positioning. Calculate your target: (Current service price ÷ 10) = product price starting point. Validate with survey: "Would you pay $X for this?"',
            text_zh: '💰 重要：研究产品定价 - 在您的领域找到 5-10 个类似产品。记录他们的定价：层级、功能、定位。计算您的目标：（当前服务价格 ÷ 10）= 产品价格起点。通过调查验证："您愿意为此支付 $X 吗？"',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important'
          },
          {
            text: '🔍 IMPORTANT: Analyze product competitors - Identify 5-10 products similar to what you\'re building. For each: pricing, features, reviews (what do customers love/hate?). Find your differentiation: What can you do better? What\'s missing? Document your unique angle.',
            text_zh: '🔍 重要：分析产品竞争对手 - 识别 5-10 个与您正在构建的产品类似的产品。对于每个产品：定价、功能、评论（客户喜欢/讨厌什么？）。找到您的差异化：您可以做得更好什么？缺少什么？记录您的独特角度。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important'
          },
          {
            text: '🔄 IMPORTANT: Plan hybrid service+product model - Define transition timeline: Month 1-2 (80% service, 20% product), Month 3-4 (60% service, 40% product), Month 5-6 (40% service, 60% product). Identify which clients need service vs product. Create bundle options.',
            text_zh: '🔄 重要：规划混合服务+产品模式 - 定义过渡时间表：第 1-2 个月（80% 服务，20% 产品），第 3-4 个月（60% 服务，40% 产品），第 5-6 个月（40% 服务，60% 产品）。识别哪些客户需要服务与产品。创建捆绑选项。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important'
          }
        ],
        dodCriteria: [
          'Service audit completed with top 2-3 productization candidates',
          '≥20 client survey responses collected',
          'Product format selected (course, template, software, etc.)',
          'Pricing validated with target range ($X-$Y)',
          '5+ competitor products analyzed',
          'Unique differentiation angle documented',
          'Hybrid model transition plan created'
        ],
        dodCriteria_zh: [
          '完成服务审计，确定前 2-3 个产品化候选',
          '收集 ≥20 份客户调查回复',
          '选择产品格式（课程、模板、软件等）',
          '验证定价并确定目标范围（$X-$Y）',
          '分析 5+ 个竞争对手产品',
          '记录独特的差异化角度',
          '创建混合模式过渡计划'
        ],
        timeEstimateWeeks: 2
      };
    }

    if (stage === 'In Progress' && steps[1]) {
      steps[1] = {
        ...steps[1],
        title: 'Build Product MVP',
        title_zh: '构建产品 MVP',
        description: 'Create the first version of your productized offering',
        description_zh: '创建您的产品化产品的第一个版本',
        actions: [
          {
            text: '🎯 PHASE 1: PLAN YOUR PRODUCT (Days 1-3)',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'context',
            phase: 'plan'
          },
          {
            text: '📝 CRITICAL: Outline your product structure - For Course: List modules and lessons (aim for 4-6 modules). For Template: Document your process step-by-step. For Software: Define 1-3 core features only. Keep it simple for v1.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'critical',
            phase: 'plan'
          },
          {
            text: '🛠️ IMPORTANT: Choose your tools - For Course: Teachable, Thinkific, Gumroad, or Kajabi. For Template: Notion, Figma, Google Sheets, or Canva. For Software: No-code (Bubble, Softr) or code (Next.js + AI). Pick based on your skills and budget.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 5,
            priority: 'important',
            phase: 'plan'
          },
          {
            text: '🏗️ PHASE 2: CREATE CONTENT (Days 4-14)',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'context',
            phase: 'create'
          },
          {
            text: '🎬 CRITICAL: Create core content - For Course: Record first 3 lessons (pilot content). For Template: Build template with examples and instructions. For Software: Build MVP with core feature only. Focus on quality over quantity.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 20,
            priority: 'critical',
            phase: 'create'
          },
          {
            text: '⭐ IMPORTANT: Leverage existing service content - Repurpose client deliverables, case studies, and processes. Turn your service work into product content. This saves 50%+ of creation time.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important',
            phase: 'create'
          },
          {
            text: '🚀 PHASE 3: LAUNCH SETUP (Days 15-21)',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'context',
            phase: 'launch'
          },
          {
            text: '📄 CRITICAL: Create sales page - Clear headline: "What problem does this solve?", Benefits (not features), Demo/preview of product, Testimonials from beta users, Pricing and CTA. Use Carrd, Webflow, or your course platform.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 15,
            priority: 'critical',
            phase: 'launch'
          },
          {
            text: '🧪 CRITICAL: Beta test with 5-10 clients - Offer free/discounted access to existing clients. Collect feedback: What works? What\'s confusing? What\'s missing? Iterate based on feedback before full launch.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 15,
            priority: 'critical',
            phase: 'launch'
          },
          {
            text: '💳 IMPORTANT: Set up payment processing - Use Stripe, Gumroad, or your platform\'s built-in payments. Test checkout flow end-to-end. Offer payment plans if price is >$200.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important',
            phase: 'launch'
          }
        ],
        dodCriteria: [
          'Product MVP created and functional',
          'Sales/landing page live with demo',
          '≥5 beta users tested product',
          'Feedback collected and documented',
          'Payment processing set up and tested',
          'Product ready for launch to existing clients'
        ],
        timeEstimateWeeks: 3,
        status: 'locked'
      };
    }

    if (stage === 'In Progress' && steps[2]) {
      steps[2] = {
        ...steps[2],
        title: 'Launch to Existing Clients',
        title_zh: '向现有客户发布',
        description: 'Launch your product to existing clients and network first',
        description_zh: '首先向现有客户和网络发布您的产品',
        actions: [
          {
            text: '💡 WHY START WITH CLIENTS: Your existing clients already trust you, understand your value, and are warm leads. They convert 10-20x better than cold audiences. Get your first sales here before expanding.',
            text_zh: '💡 为什么从客户开始：您的现有客户已经信任您，了解您的价值，并且是温暖的潜在客户。他们的转化率比冷受众好 10-20 倍。在扩展之前先在这里获得您的第一笔销售。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'context'
          },
          {
            text: '📧 PHASE 1: PREPARE LAUNCH (Days 1-3) - Set up email sequences and offers',
            text_zh: '📧 阶段 1：准备发布（第 1-3 天）- 设置电子邮件序列和优惠',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'context',
            phase: 'prepare'
          },
          {
            text: '📧 CRITICAL: Create launch email sequence - Email 1: Announcement ("I built something for you"), Email 2: Value/benefits, Email 3: Testimonials/social proof, Email 4: Early bird discount (48 hours), Email 5: Last chance. Space emails 2-3 days apart.',
            text_zh: '📧 关键：创建发布电子邮件序列 - 电子邮件 1：公告（"我为您构建了一些东西"），电子邮件 2：价值/好处，电子邮件 3：推荐/社会证明，电子邮件 4：早鸟折扣（48 小时），电子邮件 5：最后机会。电子邮件间隔 2-3 天。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 15,
            priority: 'critical',
            phase: 'prepare'
          },
          {
            text: '🎁 IMPORTANT: Create special client offer - Bundle: Product + 1 hour consultation at discount. Upgrade path: "Already a client? Get 50% off product." Loyalty reward: Free access for long-term clients. Make them feel special.',
            text_zh: '🎁 重要：创建特别客户优惠 - 捆绑：产品 + 1 小时咨询折扣。升级路径："已经是客户？产品享受 50% 折扣。" 忠诚奖励：长期客户免费访问。让他们感到特别。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important',
            phase: 'prepare'
          },
          {
            text: '📊 IMPORTANT: Set up tracking systems - Set up Google Analytics, email tool analytics, payment tracking. Create spreadsheet to track: contacts reached, open rates, click rates, conversions, revenue. Prepare for launch day.',
            text_zh: '📊 重要：设置跟踪系统 - 设置 Google Analytics、电子邮件工具分析、付款跟踪。创建电子表格以跟踪：联系的人数、打开率、点击率、转化率、收入。为发布日做准备。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important',
            phase: 'prepare'
          },
          {
            text: '🚀 PHASE 2: EXECUTE LAUNCH (Days 4-10) - Send emails and do personal outreach',
            text_zh: '🚀 阶段 2：执行发布（第 4-10 天）- 发送电子邮件并进行个人外展',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'context',
            phase: 'launch'
          },
          {
            text: '🎯 CRITICAL: Email all past and current clients - Segment list: current clients, past clients (last 2 years), prospects. Personalize where possible. Offer early bird discount (20-30% off). Target: 50-100 people minimum.',
            text_zh: '🎯 关键：向所有过去和现在的客户发送电子邮件 - 细分列表：当前客户、过去的客户（最近 2 年）、潜在客户。尽可能个性化。提供早鸟折扣（20-30% 折扣）。目标：至少 50-100 人。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 15,
            priority: 'critical',
            phase: 'launch'
          },
          {
            text: '📱 IMPORTANT: Personal outreach to VIP clients - Call or message your top 10-20 clients personally. Explain the product, offer exclusive access. Ask for feedback and testimonials. These become your champions.',
            text_zh: '📱 重要：向 VIP 客户进行个人外展 - 亲自致电或向您的前 10-20 名客户发送消息。解释产品，提供独家访问。征求反馈和推荐。这些人成为您的拥护者。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important',
            phase: 'launch'
          },
          {
            text: '📊 CRITICAL: Track launch metrics - Monitor: Email open rates (target >30%), Click rates (target >10%), Conversion rate (target >5%), Revenue generated, Customer feedback. Use Google Analytics + email tool analytics.',
            text_zh: '📊 关键：跟踪发布指标 - 监控：电子邮件打开率（目标 >30%），点击率（目标 >10%），转化率（目标 >5%），产生的收入，客户反馈。使用 Google Analytics + 电子邮件工具分析。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'critical',
            phase: 'launch'
          },
          {
            text: '📈 PHASE 3: OPTIMIZE & ITERATE (Days 11-14) - Collect feedback and improve',
            text_zh: '📈 阶段 3：优化和迭代（第 11-14 天）- 收集反馈并改进',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'context',
            phase: 'optimize'
          },
          {
            text: '💬 IMPORTANT: Collect testimonials and case studies - Ask early buyers: "What results did you get?", "What did you like most?", "Would you recommend to others?". Get permission to use quotes. Video testimonials are gold.',
            text_zh: '💬 重要：收集推荐和案例研究 - 询问早期买家："您获得了什么结果？"，"您最喜欢什么？"，"您会向其他人推荐吗？"。获得使用引用的许可。视频推荐是黄金。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important',
            phase: 'optimize'
          },
          {
            text: '🔄 IMPORTANT: Iterate based on feedback - Fix critical issues immediately. Add most-requested features to roadmap. Improve onboarding based on questions. Update sales page with testimonials. Ship improvements weekly.',
            text_zh: '🔄 重要：根据反馈进行迭代 - 立即修复关键问题。将最受欢迎的功能添加到路线图。根据问题改进入职。使用推荐更新销售页面。每周发布改进。',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important',
            phase: 'optimize'
          }
        ],
        dodCriteria: [
          'Launch email sequence sent to ≥50 existing clients',
          'Personal outreach to top 10-20 VIP clients completed',
          '≥20 product sales/signups from existing network',
          '≥5 customer testimonials collected',
          'Launch metrics tracked and analyzed',
          'Product improvements implemented based on feedback'
        ],
        dodCriteria_zh: [
          '向 ≥50 个现有客户发送发布电子邮件序列',
          '完成对前 10-20 名 VIP 客户的个人外展',
          '从现有网络获得 ≥20 个产品销售/注册',
          '收集 ≥5 个客户推荐',
          '跟踪并分析发布指标',
          '根据反馈实施产品改进'
        ],
        timeEstimateWeeks: 2,
        status: 'locked'
      };
    }

    // Add new Step 3: Expand Market Reach
    if (stage === 'In Progress' && steps.length >= 3) {
      // Insert new step 3, push old step 2 content here
      const newStep3: GeneratedStep = {
        stepIndex: 3,
        title: 'Expand Market Reach',
        description: 'Scale beyond your network to reach new audiences',
        actions: [
          {
            text: '🎯 WEEK 1: IDENTIFY & TEST CHANNELS',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'context',
            phase: 'week1'
          },
          {
            text: '📊 CRITICAL: Analyze your existing channels - Review where your service clients came from: referrals, LinkedIn, content, etc. Identify which channels have highest quality leads. For each channel, estimate: reach, conversion rate, cost per lead. Select top 3 channels to focus on.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'critical',
            phase: 'week1'
          },
          {
            text: '🧪 IMPORTANT: Test 2-3 new channels - Pick from: Content marketing (blog/SEO), Social media (LinkedIn/Twitter), Email marketing, Partnerships, Paid ads. Run 1-week test for each. Track: impressions, clicks, signups, cost. Keep channels with <$20 cost per signup.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important',
            phase: 'week1'
          },
          {
            text: '📝 WEEK 2-3: CONTENT & COMMUNITY',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'context',
            phase: 'week2'
          },
          {
            text: '📅 CRITICAL: Create 30-day content calendar - Plan: 15 social posts (LinkedIn/Twitter) with tips and case studies, 4 blog posts (SEO-focused how-tos), 2-3 email campaigns, 1-2 video tutorials. Repurpose service content and client stories. Use AI tools (ChatGPT) to speed up creation.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 15,
            priority: 'critical',
            phase: 'week2'
          },
          {
            text: '⏰ IMPORTANT: Schedule and batch content - Use Buffer, Hootsuite, or Later. Batch create content (1 day per week). Post consistently: 3-5x per week minimum. Engage with comments within 2 hours. Consistency beats perfection.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important',
            phase: 'week2'
          },
          {
            text: '🌐 IMPORTANT: Post in relevant communities - Share in: Reddit (r/[your niche]), Facebook groups, LinkedIn groups, Discord servers, Slack communities. Be helpful, not spammy. Provide value first, mention product second. Target: 5-10 communities.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important',
            phase: 'week2'
          },
          {
            text: '🔗 WEEK 3-4: CONVERSION & SCALE',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 0,
            priority: 'context',
            phase: 'week3'
          },
          {
            text: '📈 CRITICAL: Build conversion funnel - Landing page: Clear headline, demo/preview, pricing, CTA. Lead magnet: Free sample, mini-course, template preview. Email sequence: 5-7 emails nurturing leads to purchase. Sales page: Benefits, testimonials, FAQ, guarantee. Set up with Mailchimp or ConvertKit.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 15,
            priority: 'critical',
            phase: 'week3'
          },
          {
            text: '📊 IMPORTANT: Set up analytics and tracking - Google Analytics: Track page views, bounce rate, time on site. Email tool: Track open rates, click rates. Payment platform: Track conversion rate, revenue. Set up goals: signup, purchase, referral. Review metrics weekly.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'important',
            phase: 'week3'
          },
          {
            text: '🤝 IMPORTANT: Build partnerships and affiliates - Identify 10-20 complementary businesses or influencers. Offer affiliate commission (20-30%). Provide marketing materials. Create win-win partnerships. Target: 3-5 active partners.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 15,
            priority: 'important',
            phase: 'week3'
          },
          {
            text: '💰 OPTIONAL: Test paid advertising - Start small: $200-500 budget. Test: Facebook/Instagram ads, Google Ads, or LinkedIn ads. Target: <$20 cost per signup. If >$20, pause and optimize. Scale what works.',
            type: 'instruction',
            pointsCost: 0,
            pointsReward: 10,
            priority: 'optional',
            phase: 'week4'
          }
        ],
        dodCriteria: [
          'Top 3 marketing channels identified and active',
          '30-day content calendar created and scheduled',
          'Conversion funnel built (landing → email → sales)',
          'Analytics tracking set up and working',
          '≥100 new product signups/sales achieved',
          'Email list of ≥200 interested prospects',
          '3+ partnerships or affiliates established'
        ],
        timeEstimateWeeks: 4,
        status: 'locked'
      };

      steps.push(newStep3);
    }

    // Special handling for Other market in Beginner stage (Service Business)
    if (stage === 'Beginner' && market === 'Other') {
      // Step 3: Validate with Clients
      if (steps[2]) {
        steps[2] = {
          ...steps[2],
          title: 'Validate with Clients',
          title_zh: '与客户验证',
          description: 'Validate your service offering directly with ideal clients through interviews and pre-sales',
          description_zh: '通过访谈和预售直接与理想客户验证您的服务产品',
          type: 'validation',
          actions: [
            {
              text: 'Select Ideal Clients: Define your ideal client profile (ICP). Who has the problem you solve? Where do they hang out? Create a list of 10-20 potential clients to contact.',
              text_zh: '选择理想客户：定义您的理想客户画像 (ICP)。谁有您要解决的问题？他们聚集在哪里？创建一个包含 10-20 个潜在客户的联系名单。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 0,
              priority: 'critical'
            },
            {
              text: 'Conduct Validation Interviews: Reach out to your list. Goal: Schedule 5-10 interviews. Ask about their challenges, current solutions, and willingness to pay. Do NOT pitch yet—listen first.',
              text_zh: '进行验证访谈：联系您的名单。目标：安排 5-10 次访谈。询问他们的挑战、当前的解决方案以及付费意愿。先不要推销——先倾听。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical'
            },
            {
              text: 'Pre-sell or Secure Beta Testers: Based on interview feedback, propose your solution. Aim to get 3-5 committed beta testers or pre-sales (deposits or signed letters of intent).',
              text_zh: '预售或获得 Beta 测试者：根据访谈反馈，提出您的解决方案。目标是获得 3-5 名承诺的 Beta 测试者或预售（定金或签署意向书）。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 20,
              priority: 'critical'
            }
          ],
          dodCriteria: [
            'Completed at least 5 validation interviews.',
            'Secured 3-5 beta testers or pre-sales.'
          ],
          dodCriteria_zh: [
            '完成至少 5 次验证访谈。',
            '获得 3-5 名 Beta 测试者或预售。'
          ]
        };
      }

      // Step 4: Launch Service & Get First Clients
      if (steps[3]) {
        steps[3] = {
          ...steps[3],
          title: 'Launch Service & Get First Clients',
          title_zh: '发布服务并获得首批客户',
          description: 'Deliver your service to beta clients and gather social proof',
          description_zh: '向 Beta 客户交付您的服务并收集社会证明',
          type: 'launch',
          actions: [
            {
              text: 'Create Service Agreement/Proposal Template: Prepare a simple contract or proposal template. Include: scope of work, timeline, pricing (even if discounted), and payment terms. Tools: PandaDoc, HelloSign, or simple Google Doc.',
              text_zh: '创建服务协议/提案模板：准备一份简单的合同或提案模板。包括：工作范围、时间表、定价（即使有折扣）和付款条款。工具：PandaDoc、HelloSign 或简单的 Google 文档。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'critical'
            },
            {
              text: 'Deliver Service to Beta Clients: Execute the service for your 3-5 secured beta clients. Focus on over-delivering value and getting them a quick win. Document the process to refine your workflow.',
              text_zh: '向 Beta 客户交付服务：为您获得的 3-5 名 Beta 客户执行服务。专注于超额交付价值并让他们快速获胜。记录流程以优化您的工作流。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 20,
              priority: 'critical'
            },
            {
              text: 'Collect Testimonials & Case Studies: After delivery, ask for feedback. "What result did you get?" "Would you recommend this?" Get permission to use their name and photo. Create 1-2 detailed case studies.',
              text_zh: '收集推荐和案例研究：交付后，询问反馈。"您得到了什么结果？""您会推荐这个吗？"获得使用他们姓名和照片的许可。创建 1-2 个详细的案例研究。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 15,
              priority: 'critical'
            },
            {
              text: 'Refine Service Offering: Based on the beta delivery, update your pricing, packages, and messaging. What was harder than expected? What did clients value most? Create your "Public Launch" offer.',
              text_zh: '优化服务产品：根据 Beta 交付，更新您的定价、套餐和信息。什么比预期的更难？客户最看重什么？创建您的"公开发布"优惠。',
              type: 'instruction',
              pointsCost: 0,
              pointsReward: 10,
              priority: 'important'
            }
          ],
          dodCriteria: [
            'Service agreement/proposal template created.',
            'Completed service delivery for at least 3 beta clients.',
            'Collected 3+ testimonials or case studies.',
            'Service offering refined based on beta client feedback.'
          ],
          dodCriteria_zh: [
            '创建服务协议/提案模板。',
            '为至少 3 名 Beta 客户完成服务交付。',
            '收集 3+ 个推荐或案例研究。',
            '根据 Beta 客户反馈优化服务产品。'
          ]
        };
      }
    }

    return steps;
  }

  /**
   * Founder-plan persona modifications
   * Focus: Rapid validation of specific idea
   * For In Progress: Emphasize rapid iteration, paid acquisition, investor metrics
   */
  private static applyFounderPlanModifications(
    steps: GeneratedStep[],
    stage: JourneyStage
  ): GeneratedStep[] {
    if (stage === 'Have Idea' && steps[0]) {
      steps[0] = {
        ...steps[0],
        title: 'Rapid Idea Validation',
        description: 'Test your riskiest assumptions quickly and cheaply',
        actions: [
          {
            text: 'Create Validation Survey',
            type: 'survey',
            pointsCost: -5,
            pointsReward: 15,
          }
        ],
        dodCriteria: [
          '≥20 validation responses collected',
          'Riskiest assumptions tested',
          'Demand signal validated (≥15% strong interest)',
          'Price sensitivity assessed'
        ]
      };
    }

    // In Progress stage modifications for founder-plan
    if (stage === 'In Progress') {
      // Step 0: Emphasize rapid iteration
      if (steps[0]) {
        const step0Actions = steps[0].actions.map(action => {
          if (action.text.includes('Prioritize Improvements')) {
            return {
              ...action,
              text: action.text + ' Focus on rapid iteration: ship improvements in 1-week sprints, A/B test everything, measure impact immediately. Speed of learning > perfection. Target: 1 major improvement shipped per week.',
              text_zh: action.text_zh + ' 专注于快速迭代：在 1 周冲刺中发布改进，对所有内容进行 A/B 测试，立即测量影响。学习速度 > 完美。目标：每周发布 1 个主要改进。'
            };
          }
          return action;
        });
        steps[0] = { ...steps[0], actions: step0Actions };
      }

      // Step 2: Emphasize paid acquisition
      if (steps[2]) {
        const step2Actions = steps[2].actions.map(action => {
          if (action.text.includes('Test paid channels')) {
            return {
              ...action,
              text: '💰 IMPORTANT: Test paid acquisition channels - Paid ads are fastest way to scale. Start with $1000-2000 budget. Test: Facebook/Instagram ads (B2C products), Google Ads (high-intent searches), LinkedIn ads (B2B products), Twitter ads (tech/startup audience). Target: <$15 cost per signup, <$50 cost per paying customer. Run tests for 7 days minimum. Scale winners aggressively: if ROI positive, increase budget 2x weekly. Track: impressions, clicks, signups, conversions, CAC, LTV. Tools: Facebook Ads Manager, Google Ads, LinkedIn Campaign Manager.',
              text_zh: '💰 重要：测试付费获取渠道 - 付费广告是扩展的最快方式。从 $1000-2000 预算开始。测试：Facebook/Instagram 广告（B2C 产品）、Google 广告（高意图搜索）、LinkedIn 广告（B2B 产品）、Twitter 广告（技术/初创公司受众）。目标：每次注册成本 <$15，每个付费客户成本 <$50。至少运行 7 天测试。积极扩大赢家：如果 ROI 为正，每周增加 2 倍预算。跟踪：展示次数、点击次数、注册次数、转化次数、CAC、LTV。工具：Facebook Ads Manager、Google Ads、LinkedIn Campaign Manager。',
              priority: 'important' as const
            };
          }
          return action;
        });
        steps[2] = { ...steps[2], actions: step2Actions };
      }

      // Step 3: Emphasize investor metrics
      if (steps[3]) {
        const step3Actions = steps[3].actions.map(action => {
          if (action.text.includes('Implement Analytics Dashboard')) {
            return {
              ...action,
              text: action.text + ' For fundraising, track investor-focused metrics: MRR growth rate (target ≥15% monthly), Net Revenue Retention (target ≥100%), Gross margin (target ≥70% for SaaS), CAC payback period (target <12 months), Rule of 40 (growth rate + profit margin ≥40%). Create investor-ready dashboard with these metrics.',
              text_zh: action.text_zh + ' 对于筹款，跟踪投资者关注的指标：MRR 增长率（目标每月 ≥15%）、净收入留存率（目标 ≥100%）、毛利率（SaaS 目标 ≥70%）、CAC 回收期（目标 <12 个月）、40 法则（增长率 + 利润率 ≥40%）。使用这些指标创建投资者就绪的仪表板。'
            };
          }
          return action;
        });
        steps[3] = { ...steps[3], actions: step3Actions };
      }
    }

    return steps;
  }
}
